import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TelegramLinks from './TelegramLinks';
import LanguageTabs from './LanguageTabs';
import { FiltersTabs } from './ui';

interface ShopInfo {
  id: number;
  name: string;
  apiKey: string;
}

type TabKey = 'general' | 'integrations' | 'design-texts' | 'withdraw-page' | 'telegram';

interface ShopSettings {
  webhookUrl?: string;
  ipAllowlist?: string;
  telegramEnabled?: boolean;
  telegramBotToken?: string;
  telegramChatId?: string;
  autoConfirmEnabled?: boolean;
  currency?: string;
  domain?: string;
  // Callback payload flags
  includeTransactionIdInCallbacks?: boolean;
  includeBuyerPaidInCallbacks?: boolean;
  include_buyer_paid_in_callbacks?: string | boolean;
  includePaymentIdInCallbacks?: boolean;
  // Настройки платежной страницы
  paymentPageTitle?: string;
  paymentPageSubtitle?: string;
  paymentPageLogo?: string;
  paymentPageLogoEnabled?: boolean;
  paymentPageLogoTransparentBg?: boolean;
  paymentPageHeaderEnabled?: boolean;
  paymentPageLogoUploaded?: string;
  paymentPublicIdEnabled?: boolean;
  paymentPagePrimaryColor?: string;
  paymentPageButtonColor?: string;
  paymentPageButtonTextColor?: string;
  paymentPageBackgroundColor?: string;
  paymentPageAccentColor?: string;
  // Новые общие цвета интерфейса
  paymentPageTextColor?: string;
  paymentPageTextSecondary?: string;
  paymentPageBorderColor?: string;
  paymentPageCardBg?: string;
  paymentPageAmountTextColor?: string;
  paymentPageExpiryBorderColor?: string;
  paymentPageShowAmount?: boolean;
  paymentPageShowCurrency?: boolean;
  paymentPageFeaturesEnabled?: boolean;
  paymentPageCustomCSS?: string;
  paymentPageCustomJS?: string;
  // Новый переключатель дизайна
  paymentPageDesign?: 'classic' | 'simple';
  // Simple design specific
  paymentPageSimpleSuccessRedirectSeconds?: number;
  paymentPageSimpleReturnUrl?: string;
  paymentPageNoUrlBehavior?: 'none' | 'close' | 'payment';
  paymentPageSimpleInstructionRu?: string;
  paymentPageSimpleInstructionKz?: string;
  paymentPageSimpleInstructionEn?: string;
  paymentPageSimpleInstruction2Ru?: string;
  paymentPageSimpleInstruction2Kz?: string;
  paymentPageSimpleInstruction2En?: string;
  // Simple UI visual toggles
  paymentPageSimpleAmountBorderEnabled?: boolean;
  paymentPageSimpleStepNumberStyle?: 'numeric' | 'roman';
  paymentPageSimpleStepNumberAsBadge?: boolean;
  // Simple UI copy button
  paymentPageSimpleCopyButtonStyle?: 'text' | 'icon';
  paymentPageSimpleCopyButtonIcon?: string;
  // Simple design colors per step and recipient
  paymentPageSimpleStep1Color?: string;
  paymentPageSimpleStep2Color?: string;
  paymentPageSimpleStep3Color?: string;
  paymentPageSimpleStep4Color?: string;
  // Simple design note colors per step
  paymentPageSimpleStep1NoteColor?: string;
  paymentPageSimpleStep2NoteColor?: string;
  paymentPageSimpleStep3NoteColor?: string;
  paymentPageSimpleStep4NoteColor?: string;
  paymentPageSimpleRecipientColor?: string;
  // Simple design step titles and notes
  paymentPageSimpleStep1TitleRu?: string;
  paymentPageSimpleStep1TitleKz?: string;
  paymentPageSimpleStep1TitleEn?: string;
  paymentPageSimpleStep1NoteRu?: string;
  paymentPageSimpleStep1NoteKz?: string;
  paymentPageSimpleStep1NoteEn?: string;
  paymentPageSimpleStep2TitleRu?: string;
  paymentPageSimpleStep2TitleKz?: string;
  paymentPageSimpleStep2TitleEn?: string;
  paymentPageSimpleStep2NoteRu?: string;
  paymentPageSimpleStep2NoteKz?: string;
  paymentPageSimpleStep2NoteEn?: string;
  paymentPageSimpleStep3TitleRu?: string;
  paymentPageSimpleStep3TitleKz?: string;
  paymentPageSimpleStep3TitleEn?: string;
  paymentPageSimpleStep3NoteRu?: string;
  paymentPageSimpleStep3NoteKz?: string;
  paymentPageSimpleStep3NoteEn?: string;
  // Step 4
  paymentPageSimpleStep4TitleRu?: string;
  paymentPageSimpleStep4TitleKz?: string;
  paymentPageSimpleStep4TitleEn?: string;
  paymentPageSimpleStep4NoteRu?: string;
  paymentPageSimpleStep4NoteKz?: string;
  paymentPageSimpleStep4NoteEn?: string;
  // Simple design modals customization
  paymentPageSimpleSuccessBgColor?: string;
  paymentPageSimpleSuccessIcon?: string;
  paymentPageSimpleSuccessTitleRu?: string;
  paymentPageSimpleSuccessTitleKz?: string;
  paymentPageSimpleSuccessTitleEn?: string;
  paymentPageSimpleSuccessMessageRu?: string;
  paymentPageSimpleSuccessMessageKz?: string;
  paymentPageSimpleSuccessMessageEn?: string;
  paymentPageSimpleFailureBgColor?: string;
  paymentPageSimpleFailureIcon?: string;
  paymentPageSimpleFailureTitleRu?: string;
  paymentPageSimpleFailureTitleKz?: string;
  paymentPageSimpleFailureTitleEn?: string;
  paymentPageSimpleFailureMessageRu?: string;
  paymentPageSimpleFailureMessageKz?: string;
  paymentPageSimpleFailureMessageEn?: string;
  
  // Новые поля для футера
  paymentPageFooterText?: string;
  paymentPageFooterTextRu?: string;
  paymentPageFooterTextKz?: string;
  paymentPageFooterTextEn?: string;
  paymentPageFooterIcon?: string;
  paymentPageFooterLink?: string;
  paymentPageFooterBadgeSslRu?: string;
  paymentPageFooterBadgeSslKz?: string;
  paymentPageFooterBadgeSslEn?: string;
  paymentPageFooterBadgeSecureRu?: string;
  paymentPageFooterBadgeSecureKz?: string;
  paymentPageFooterBadgeSecureEn?: string;
  paymentPageFooterBadgeSupportRu?: string;
  paymentPageFooterBadgeSupportKz?: string;
  paymentPageFooterBadgeSupportEn?: string;
  paymentPageFooterEnabled?: boolean;
  // Новые поля для сумм
  paymentPageDefaultAmount?: number;
  paymentPageDefaultCurrency?: string;
  paymentPageAmountOptions?: string;
  paymentPageCustomAmountEnabled?: boolean;
  paymentPageMinAmount?: number;
  paymentPageLanguage?: string;
  // Многоязычные поля
  paymentPageFormHeaderRu: string;
  paymentPageFormHeaderKz: string;
  paymentPageFormHeaderEn: string;
  paymentPageFormSubheaderRu: string;
  paymentPageFormSubheaderKz: string;
  paymentPageFormSubheaderEn: string;
  paymentPageAmountCaptionRu: string;
  paymentPageAmountCaptionKz: string;
  paymentPageAmountCaptionEn: string;
  paymentPageAmountHintRu: string;
  paymentPageAmountHintKz: string;
  paymentPageAmountHintEn: string;
  paymentPageQuickSelectHeaderRu: string;
  paymentPageQuickSelectHeaderKz: string;
  paymentPageQuickSelectHeaderEn: string;
  paymentPageCommentCaptionRu: string;
  paymentPageCommentCaptionKz: string;
  paymentPageCommentCaptionEn: string;
  paymentPageSubmitButtonRu: string;
  paymentPageSubmitButtonKz: string;
  paymentPageSubmitButtonEn: string;
  paymentPageProcessingRu: string;
  paymentPageProcessingKz: string;
  paymentPageProcessingEn: string;
  paymentPageSuccessTitleRu: string;
  paymentPageSuccessTitleKz: string;
  paymentPageSuccessTitleEn: string;
  paymentPageSuccessMessageRu: string;
  paymentPageSuccessMessageKz: string;
  paymentPageSuccessMessageEn: string;
  paymentPageAmountToPayRu: string;
  paymentPageAmountToPayKz: string;
  paymentPageAmountToPayEn: string;
  paymentPageOriginalAmountRu: string;
  paymentPageOriginalAmountKz: string;
  paymentPageOriginalAmountEn: string;
  paymentPageRequisitesRu: string;
  paymentPageRequisitesKz: string;
  paymentPageRequisitesEn: string;
  paymentPageExpiresAtRu: string;
  paymentPageExpiresAtKz: string;
  paymentPageExpiresAtEn: string;
  paymentPageErrorRu: string;
  paymentPageErrorKz: string;
  paymentPageErrorEn: string;
  // Alt-текст логотипа (мультиязычный)
  paymentPageLogoAltRu?: string;
  paymentPageLogoAltKz?: string;
  paymentPageLogoAltEn?: string;
  // Новые поля для заголовков и карточек преимуществ
  paymentPageTitleRu?: string;
  paymentPageTitleKz?: string;
  paymentPageTitleEn?: string;
  paymentPageSubtitleRu?: string;
  paymentPageSubtitleKz?: string;
  paymentPageSubtitleEn?: string;
  paymentPageFeature1TitleRu?: string;
  paymentPageFeature1TitleKz?: string;
  paymentPageFeature1TitleEn?: string;
  paymentPageFeature1SubtitleRu?: string;
  paymentPageFeature1SubtitleKz?: string;
  paymentPageFeature1SubtitleEn?: string;
  paymentPageFeature1IconRu?: string;
  paymentPageFeature1IconKz?: string;
  paymentPageFeature1IconEn?: string;
  paymentPageFeature2TitleRu?: string;
  paymentPageFeature2TitleKz?: string;
  paymentPageFeature2TitleEn?: string;
  paymentPageFeature2SubtitleRu?: string;
  paymentPageFeature2SubtitleKz?: string;
  paymentPageFeature2SubtitleEn?: string;
  paymentPageFeature2IconRu?: string;
  paymentPageFeature2IconKz?: string;
  paymentPageFeature2IconEn?: string;
  paymentPageFeature3TitleRu?: string;
  paymentPageFeature3TitleKz?: string;
  paymentPageFeature3TitleEn?: string;
  paymentPageFeature3SubtitleRu?: string;
  paymentPageFeature3SubtitleKz?: string;
  paymentPageFeature3SubtitleEn?: string;
  paymentPageFeature3IconRu?: string;
  paymentPageFeature3IconKz?: string;
  paymentPageFeature3IconEn?: string;
  // Поля для кнопок сумм
  paymentPageAmountButtons?: Array<{
    amount: number;
    label?: string;
  }>;
  
  // Shop1 Integration Settings
  shop1Enabled?: boolean;
  shop1HmacPassword?: string;
  shop1HmacPasswordSet?: boolean;
  shop1AllowedIps?: string;
  shop1DefaultCallbackUrl?: string;
  shop1DefaultSuccessUrl?: string;    // ⭐ НОВОЕ
  shop1DefaultFailureUrl?: string;    // ⭐ НОВОЕ
  shop1CallbackRetryMax?: number;
  shop1CallbackRetryBackoffMs?: number;
  paymentTtlMinutes?: number;
  paymentAmountDivisor?: number;
  withdrawAmountDivisor?: number;
  // Кнопка подтверждения и шаги Simple (1-4)
  paymentPageConfirmButtonUrl?: string;
  
  // ===== Настройки страницы вывода средств =====
  withdrawPageEnabled?: boolean;
  withdrawPageDesign?: 'classic' | 'simple';
  withdrawPageTitle?: string;
  withdrawPageSubtitle?: string;
  withdrawPagePrimaryColor?: string;
  withdrawPageButtonColor?: string;
  withdrawPageButtonTextColor?: string;
  withdrawPageBackgroundColor?: string;
  withdrawPageAccentColor?: string;
  withdrawPageCustomCSS?: string;
  withdrawPageCustomJS?: string;
  withdrawPageTextColor?: string;
  withdrawPageAmountTextColor?: string;
  withdrawPageLogoEnabled?: boolean;
  withdrawPageFooterEnabled?: boolean;
  // Новые цвета карточки вывода
  withdrawPageCardBg?: string;
  withdrawPageBorderColor?: string;
  
  // Мультиязычные заголовки
  withdrawPageTitleRu?: string;
  withdrawPageTitleKz?: string;
  withdrawPageTitleEn?: string;
  withdrawPageSubtitleRu?: string;
  withdrawPageSubtitleKz?: string;
  withdrawPageSubtitleEn?: string;
  
  // Заголовки формы
  withdrawPageFormHeaderRu?: string;
  withdrawPageFormHeaderKz?: string;
  withdrawPageFormHeaderEn?: string;
  withdrawPageFormSubheaderRu?: string;
  withdrawPageFormSubheaderKz?: string;
  withdrawPageFormSubheaderEn?: string;
  
  // Подписи полей
  withdrawPageAmountCaptionRu?: string;
  withdrawPageAmountCaptionKz?: string;
  withdrawPageAmountCaptionEn?: string;
  withdrawPageCardCaptionRu?: string;
  withdrawPageCardCaptionKz?: string;
  withdrawPageCardCaptionEn?: string;
  withdrawPageNameCaptionRu?: string;
  withdrawPageNameCaptionKz?: string;
  withdrawPageNameCaptionEn?: string;
  
  // Кнопки и статусы
  withdrawPageSubmitButtonRu?: string;
  withdrawPageSubmitButtonKz?: string;
  withdrawPageSubmitButtonEn?: string;
  withdrawPageEditButtonRu?: string;
  withdrawPageEditButtonKz?: string;
  withdrawPageEditButtonEn?: string;
  withdrawPageProcessingRu?: string;
  withdrawPageProcessingKz?: string;
  withdrawPageProcessingEn?: string;
  withdrawPageSuccessTitleRu?: string;
  withdrawPageSuccessTitleKz?: string;
  withdrawPageSuccessTitleEn?: string;
  withdrawPageSuccessMessageRu?: string;
  withdrawPageSuccessMessageKz?: string;
  withdrawPageSuccessMessageEn?: string;
  withdrawPageErrorRu?: string;
  withdrawPageErrorKz?: string;
  withdrawPageErrorEn?: string;
  
  // Сообщения для пользователей
  withdrawPageExistingUserMessageRu?: string;
  withdrawPageExistingUserMessageKz?: string;
  withdrawPageExistingUserMessageEn?: string;
  withdrawPageNewUserMessageRu?: string;
  withdrawPageNewUserMessageKz?: string;
  withdrawPageNewUserMessageEn?: string;
  
  // Настройки безопасности
  withdrawPageAllowEdit?: boolean;
  withdrawPageRequireConfirmation?: boolean;
  
  // Плитки преимуществ
  withdrawPageFeature1TitleRu?: string;
  withdrawPageFeature1TitleKz?: string;
  withdrawPageFeature1TitleEn?: string;
  withdrawPageFeature1SubtitleRu?: string;
  withdrawPageFeature1SubtitleKz?: string;
  withdrawPageFeature1SubtitleEn?: string;
  withdrawPageFeature1IconRu?: string;
  withdrawPageFeature1IconKz?: string;
  withdrawPageFeature1IconEn?: string;
  withdrawPageFeature2TitleRu?: string;
  withdrawPageFeature2TitleKz?: string;
  withdrawPageFeature2TitleEn?: string;
  withdrawPageFeature2SubtitleRu?: string;
  withdrawPageFeature2SubtitleKz?: string;
  withdrawPageFeature2SubtitleEn?: string;
  withdrawPageFeature2IconRu?: string;
  withdrawPageFeature2IconKz?: string;
  withdrawPageFeature2IconEn?: string;
  withdrawPageFeature3TitleRu?: string;
  withdrawPageFeature3TitleKz?: string;
  withdrawPageFeature3TitleEn?: string;
  withdrawPageFeature3SubtitleRu?: string;
  withdrawPageFeature3SubtitleKz?: string;
  withdrawPageFeature3SubtitleEn?: string;
  withdrawPageFeature3IconRu?: string;
  withdrawPageFeature3IconKz?: string;
  withdrawPageFeature3IconEn?: string;
}

const ShopSettings: React.FC = () => {
  const { user, logout } = useAuth();

  // Функция для извлечения device name из метаданных сообщения провайдера
  const getDeviceNameFromMeta = (meta: string | null): string => {
    if (!meta) return 'Не указано';
    try {
      const metaData = JSON.parse(meta);
      return metaData?.deviceInfo?.name || 'Не указано';
    } catch {
      return 'Не указано';
    }
  };
  const [shopInfo, setShopInfo] = useState<ShopInfo | null>(null);
  const [settings, setSettings] = useState<ShopSettings>({
    paymentPageFormHeaderRu: '',
    paymentPageFormHeaderKz: '',
    paymentPageFormHeaderEn: '',
    paymentPageFormSubheaderRu: '',
    paymentPageFormSubheaderKz: '',
    paymentPageFormSubheaderEn: '',
    paymentPageAmountCaptionRu: '',
    paymentPageAmountCaptionKz: '',
    paymentPageAmountCaptionEn: '',
    paymentPageAmountHintRu: '',
    paymentPageAmountHintKz: '',
    paymentPageAmountHintEn: '',
    paymentPageQuickSelectHeaderRu: '',
    paymentPageQuickSelectHeaderKz: '',
    paymentPageQuickSelectHeaderEn: '',
    paymentPageCommentCaptionRu: '',
    paymentPageFooterEnabled: true,
    paymentPageCommentCaptionKz: '',
    paymentPageCommentCaptionEn: '',
    paymentPageSubmitButtonRu: '',
    paymentPageSubmitButtonKz: '',
    paymentPageSubmitButtonEn: '',
    paymentPageProcessingRu: '',
    paymentPageProcessingKz: '',
    paymentPageProcessingEn: '',
    paymentPageSuccessTitleRu: '',
    paymentPageSuccessTitleKz: '',
    paymentPageSuccessTitleEn: '',
    paymentPageSuccessMessageRu: '',
    paymentPageSuccessMessageKz: '',
    paymentPageSuccessMessageEn: '',
    paymentPageAmountToPayRu: '',
    paymentPageAmountToPayKz: '',
    paymentPageAmountToPayEn: '',
    paymentPageOriginalAmountRu: '',
    paymentPageOriginalAmountKz: '',
    paymentPageOriginalAmountEn: '',
    paymentPageRequisitesRu: '',
    paymentPageRequisitesKz: '',
    paymentPageRequisitesEn: '',
    paymentPageExpiresAtRu: '',
    paymentPageExpiresAtKz: '',
    paymentPageExpiresAtEn: '',
    paymentPageErrorRu: '',
    paymentPageErrorKz: '',
    paymentPageErrorEn: '',
    // Simple design specific defaults
    paymentPageSimpleSuccessRedirectSeconds: 3,
    paymentPageNoUrlBehavior: 'none',
    paymentPageSimpleInstructionRu: '',
    paymentPageSimpleInstructionKz: '',
    paymentPageSimpleInstructionEn: '',
    paymentPageSimpleInstruction2Ru: '',
    paymentPageSimpleInstruction2Kz: '',
    paymentPageSimpleInstruction2En: '',
    // Simple UI visual toggles defaults
    paymentPageSimpleAmountBorderEnabled: true,
    paymentPageSimpleStepNumberStyle: 'numeric',
    paymentPageSimpleStepNumberAsBadge: true,
    paymentPageSimpleCopyButtonStyle: 'text',
    paymentPageSimpleCopyButtonIcon: '📋',
    // Simple step defaults
    paymentPageSimpleStep1TitleRu: 'Оплата в банке',
    paymentPageSimpleStep1TitleKz: 'Банкте төлеу',
    paymentPageSimpleStep1TitleEn: 'Pay in bank',
    paymentPageSimpleStep2TitleRu: 'Совершите перевод на карту',
    paymentPageSimpleStep2TitleKz: 'Картаға аударыңыз',
    paymentPageSimpleStep2TitleEn: 'Transfer to the card',
    paymentPageSimpleStep3TitleRu: 'Подтвердите перевод',
    paymentPageSimpleStep3TitleKz: 'Аударымды растаңыз',
    paymentPageSimpleStep3TitleEn: 'Confirm transfer',
    paymentPageTitleRu: '',
    paymentPageTitleKz: '',
    paymentPageTitleEn: '',
    paymentPageSubtitleRu: '',
    paymentPageSubtitleKz: '',
    paymentPageSubtitleEn: '',
    paymentPageFeature1TitleRu: '',
    paymentPageFeature1TitleKz: '',
    paymentPageFeature1TitleEn: '',
    paymentPageFeature1SubtitleRu: '',
    paymentPageFeature1SubtitleKz: '',
    paymentPageFeature1SubtitleEn: '',
    paymentPageFeature1IconRu: '',
    paymentPageFeature1IconKz: '',
    paymentPageFeature1IconEn: '',
    paymentPageFeature2TitleRu: '',
    paymentPageFeature2TitleKz: '',
    paymentPageFeature2TitleEn: '',
    paymentPageFeature2SubtitleRu: '',
    paymentPageFeature2SubtitleKz: '',
    paymentPageFeature2SubtitleEn: '',
    paymentPageFeature2IconRu: '',
    paymentPageFeature2IconKz: '',
    paymentPageFeature2IconEn: '',
    paymentPageFeature3TitleRu: '',
    paymentPageFeature3TitleKz: '',
    paymentPageFeature3TitleEn: '',
    paymentPageFeature3SubtitleRu: '',
    paymentPageFeature3SubtitleKz: '',
    paymentPageFeature3SubtitleEn: '',
    paymentPageFeature3IconRu: '',
    paymentPageFeature3IconKz: '',
    paymentPageFeature3IconEn: '',
    // Инициализация кнопок сумм
    paymentPageAmountButtons: [],
    
    // Shop1 Integration defaults
    shop1Enabled: false,
    shop1HmacPassword: '',
    shop1HmacPasswordSet: false,
    shop1AllowedIps: '',
    shop1DefaultCallbackUrl: '',
    shop1DefaultSuccessUrl: '',      // ⭐ НОВОЕ
    shop1DefaultFailureUrl: '',      // ⭐ НОВОЕ
    shop1CallbackRetryMax: 10,
    shop1CallbackRetryBackoffMs: 30000,
    paymentTtlMinutes: undefined,
    // новый параметр делителя суммы (по умолчанию отображаем 1)
    paymentAmountDivisor: 1,
    
    // ===== Настройки страницы вывода средств =====
    withdrawPageEnabled: true,
    withdrawPageDesign: 'simple',
    withdrawPageTitle: '',
    withdrawPageSubtitle: '',
    withdrawPagePrimaryColor: '#10B981',
    withdrawPageButtonColor: '#10B981',
    withdrawPageButtonTextColor: '#FFFFFF',
    withdrawPageBackgroundColor: '',
    withdrawPageAccentColor: '#059669',
    withdrawPageCustomCSS: '',
    withdrawPageCustomJS: '',
    withdrawPageTextColor: '',
    withdrawPageAmountTextColor: '',
    withdrawPageLogoEnabled: true,
    withdrawPageFooterEnabled: true,
    
    // Мультиязычные заголовки
    withdrawPageTitleRu: '',
    withdrawPageTitleKz: '',
    withdrawPageTitleEn: '',
    withdrawPageSubtitleRu: '',
    withdrawPageSubtitleKz: '',
    withdrawPageSubtitleEn: '',
    
    // Заголовки формы
    withdrawPageFormHeaderRu: '',
    withdrawPageFormHeaderKz: '',
    withdrawPageFormHeaderEn: '',
    withdrawPageFormSubheaderRu: '',
    withdrawPageFormSubheaderKz: '',
    withdrawPageFormSubheaderEn: '',
    
    // Подписи полей
    withdrawPageAmountCaptionRu: '',
    withdrawPageAmountCaptionKz: '',
    withdrawPageAmountCaptionEn: '',
    withdrawPageCardCaptionRu: '',
    withdrawPageCardCaptionKz: '',
    withdrawPageCardCaptionEn: '',
    withdrawPageNameCaptionRu: '',
    withdrawPageNameCaptionKz: '',
    withdrawPageNameCaptionEn: '',
    
    // Кнопки и статусы
    withdrawPageSubmitButtonRu: '',
    withdrawPageSubmitButtonKz: '',
    withdrawPageSubmitButtonEn: '',
    withdrawPageEditButtonRu: '',
    withdrawPageEditButtonKz: '',
    withdrawPageEditButtonEn: '',
    withdrawPageProcessingRu: '',
    withdrawPageProcessingKz: '',
    withdrawPageProcessingEn: '',
    withdrawPageSuccessTitleRu: '',
    withdrawPageSuccessTitleKz: '',
    withdrawPageSuccessTitleEn: '',
    withdrawPageSuccessMessageRu: '',
    withdrawPageSuccessMessageKz: '',
    withdrawPageSuccessMessageEn: '',
    withdrawPageErrorRu: '',
    withdrawPageErrorKz: '',
    withdrawPageErrorEn: '',
    
    // Сообщения для пользователей
    withdrawPageExistingUserMessageRu: '',
    withdrawPageExistingUserMessageKz: '',
    withdrawPageExistingUserMessageEn: '',
    withdrawPageNewUserMessageRu: '',
    withdrawPageNewUserMessageKz: '',
    withdrawPageNewUserMessageEn: '',
    
    // Настройки безопасности
    withdrawPageAllowEdit: true,
    withdrawPageRequireConfirmation: true,
    
    // Плитки преимуществ
    withdrawPageFeature1TitleRu: '',
    withdrawPageFeature1TitleKz: '',
    withdrawPageFeature1TitleEn: '',
    withdrawPageFeature1SubtitleRu: '',
    withdrawPageFeature1SubtitleKz: '',
    withdrawPageFeature1SubtitleEn: '',
    withdrawPageFeature1IconRu: '',
    withdrawPageFeature1IconKz: '',
    withdrawPageFeature1IconEn: '',
    withdrawPageFeature2TitleRu: '',
    withdrawPageFeature2TitleKz: '',
    withdrawPageFeature2TitleEn: '',
    withdrawPageFeature2SubtitleRu: '',
    withdrawPageFeature2SubtitleKz: '',
    withdrawPageFeature2SubtitleEn: '',
    withdrawPageFeature2IconRu: '',
    withdrawPageFeature2IconKz: '',
    withdrawPageFeature2IconEn: '',
    withdrawPageFeature3TitleRu: '',
    withdrawPageFeature3TitleKz: '',
    withdrawPageFeature3TitleEn: '',
    withdrawPageFeature3SubtitleRu: '',
    withdrawPageFeature3SubtitleKz: '',
    withdrawPageFeature3SubtitleEn: '',
    withdrawPageFeature3IconRu: '',
    withdrawPageFeature3IconKz: '',
    withdrawPageFeature3IconEn: '',
    // Callbacks defaults
    includeTransactionIdInCallbacks: false,
    includeBuyerPaidInCallbacks: false,
    includePaymentIdInCallbacks: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string>('');
  const [webhookTestUrl, setWebhookTestUrl] = useState<string>('');
  const [webhookTestResult, setWebhookTestResult] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabKey>('general');
  const tabItems = useMemo(() => [
    { key: 'general' as TabKey, label: 'Основные' },
    { key: 'integrations' as TabKey, label: 'API и интеграции' },
    { key: 'design-texts' as TabKey, label: 'Дизайн и тексты' },
    { key: 'withdraw-page' as TabKey, label: 'Страница вывода' },
    { key: 'telegram' as TabKey, label: 'Telegram' },
  ], []);
  const [designSubTab, setDesignSubTab] = useState<'common' | 'simple' | 'classic'>('simple');
  const [withdrawLanguage, setWithdrawLanguage] = useState<'ru' | 'kz' | 'en'>('ru');

  const availableCurrencies = [
    { code: 'RUB', name: 'Российский рубль (₽)' },
    { code: 'KZT', name: 'Казахстанский тенге (₸)' },
    { code: 'USD', name: 'Доллар США ($)' },
    { code: 'EUR', name: 'Евро (€)' }
  ];

  

  const presetColors = [
    { name: 'Синий', value: '#3B82F6' },
    { name: 'Зеленый', value: '#10B981' },
    { name: 'Фиолетовый', value: '#8B5CF6' },
    { name: 'Красный', value: '#EF4444' },
    { name: 'Оранжевый', value: '#F59E0B' },
    { name: 'Серый', value: '#6B7280' }
  ];
  // Исправляем проблему с бесконечными перерендерами
  const fetchShopSettings = useCallback(async () => {
    if (user?.role !== 'admin') return;
    
    try {
      const response = await fetch('/api/admin/shop/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Received settings data:', data); // Отладочная информация
        setShopInfo(data.shop);
          const newSettings = {
          webhookUrl: data.webhookUrl || '',
          ipAllowlist: data.ipAllowlist || '',
          telegramEnabled: !!data.telegramEnabled,
          telegramBotToken: data.telegramBotToken || '',
          telegramChatId: data.telegramChatId || '',
          autoConfirmEnabled: typeof data.autoConfirmEnabled === 'boolean' ? data.autoConfirmEnabled : (data.autoConfirmEnabled === '1'),
          currency: data.currency || 'RUB',
          domain: data.domain || '',
          // Shop1 Integration
          shop1Enabled: !!data.shop1Enabled,
          shop1HmacPassword: '',
          shop1HmacPasswordSet: !!data.shop1HmacPasswordSet,
          shop1AllowedIps: data.shop1AllowedIps || '',
          shop1DefaultCallbackUrl: data.shop1DefaultCallbackUrl || '',
          shop1DefaultSuccessUrl: data.shop1DefaultSuccessUrl || '',
          shop1DefaultFailureUrl: data.shop1DefaultFailureUrl || '',
          shop1CallbackRetryMax: typeof data.shop1CallbackRetryMax === 'number' ? data.shop1CallbackRetryMax : 10,
          shop1CallbackRetryBackoffMs: typeof data.shop1CallbackRetryBackoffMs === 'number' ? data.shop1CallbackRetryBackoffMs : 30000,
          shop1SignatureAlgo: data.shop1SignatureAlgo || 'sha512_base64',
          // Настройки платежной страницы
          paymentPageTitle: data.paymentPageTitle || '',
          paymentPageSubtitle: data.paymentPageSubtitle || '',
          paymentPageLogo: data.paymentPageLogo || '',
          paymentPageLogoUploaded: data.paymentPageLogoUploaded || '',
          paymentPageLogoEnabled: typeof data.paymentPageLogoEnabled === 'boolean' ? data.paymentPageLogoEnabled : true,
          paymentPublicIdEnabled: !!(data as any).paymentPublicIdEnabled,
          paymentPageHeaderEnabled: typeof (data as any).paymentPageHeaderEnabled === 'boolean' ? (data as any).paymentPageHeaderEnabled : true,
          paymentPagePrimaryColor: data.paymentPagePrimaryColor || '#3B82F6',
          paymentPageButtonColor: data.paymentPageButtonColor || data.paymentPagePrimaryColor || '#3B82F6',
          paymentPageButtonTextColor: data.paymentPageButtonTextColor || '#FFFFFF',
          paymentPageBackgroundColor: data.paymentPageBackgroundColor || '',
          paymentPageAccentColor: data.paymentPageAccentColor || '#10B981',
          paymentPageAmountTextColor: data.paymentPageAmountTextColor || '#2563EB',
          paymentPageExpiryBorderColor: data.paymentPageExpiryBorderColor || '#FED7AA',
          // Новые общие цвета интерфейса
          paymentPageTextColor: data.paymentPageTextColor || '#1F2937',
          paymentPageTextSecondary: data.paymentPageTextSecondary || '#6B7280',
          paymentPageBorderColor: data.paymentPageBorderColor || '#E5E7EB',
          paymentPageCardBg: data.paymentPageCardBg || '#FFFFFF',
          paymentPageShowAmount: data.paymentPageShowAmount || false,
          paymentPageShowCurrency: data.paymentPageShowCurrency || false,
          paymentPageFeaturesEnabled: typeof data.paymentPageFeaturesEnabled === 'boolean' ? data.paymentPageFeaturesEnabled : true,
          paymentPageCustomCSS: data.paymentPageCustomCSS || '',
          paymentPageCustomJS: data.paymentPageCustomJS || '',

          // Новые поля для футера
          paymentPageFooterText: data.paymentPageFooterText || '',
          paymentPageFooterTextRu: data.paymentPageFooterTextRu || data.paymentPageFooterText || '',
          paymentPageFooterTextKz: data.paymentPageFooterTextKz || data.paymentPageFooterText || '',
          paymentPageFooterTextEn: data.paymentPageFooterTextEn || data.paymentPageFooterText || '',
          paymentPageFooterIcon: data.paymentPageFooterIcon || '',
          paymentPageFooterLink: data.paymentPageFooterLink || '',
          paymentPageFooterBadgeSslRu: data.paymentPageFooterBadgeSslRu || 'SSL защита',
          paymentPageFooterBadgeSslKz: data.paymentPageFooterBadgeSslKz || 'SSL қорғау',
          paymentPageFooterBadgeSslEn: data.paymentPageFooterBadgeSslEn || 'SSL secure',
          paymentPageFooterBadgeSecureRu: data.paymentPageFooterBadgeSecureRu || 'Безопасные платежи',
          paymentPageFooterBadgeSecureKz: data.paymentPageFooterBadgeSecureKz || 'Қауіпсіз төлемдер',
          paymentPageFooterBadgeSecureEn: data.paymentPageFooterBadgeSecureEn || 'Secure payments',
          paymentPageFooterBadgeSupportRu: data.paymentPageFooterBadgeSupportRu || '24/7 поддержка',
          paymentPageFooterBadgeSupportKz: data.paymentPageFooterBadgeSupportKz || '24/7 қолдау',
          paymentPageFooterBadgeSupportEn: data.paymentPageFooterBadgeSupportEn || '24/7 support',
          paymentPageFooterEnabled: (data as any).paymentPageFooterEnabled !== false,
          // Новые поля для сумм
          paymentPageDefaultAmount: data.paymentPageDefaultAmount || 0,
          paymentPageDefaultCurrency: data.paymentPageDefaultCurrency || 'RUB',
          paymentPageAmountOptions: data.paymentPageAmountOptions || '',
          paymentPageCustomAmountEnabled: data.paymentPageCustomAmountEnabled || false,
          paymentPageMinAmount: data.paymentPageMinAmount || 100,
          paymentPageLanguage: data.paymentPageLanguage || 'ru',
          // Многоязычные поля
          paymentPageFormHeaderRu: data.paymentPageFormHeaderRu || '',
          paymentPageFormHeaderKz: data.paymentPageFormHeaderKz || '',
          paymentPageFormHeaderEn: data.paymentPageFormHeaderEn || '',
          paymentPageFormSubheaderRu: data.paymentPageFormSubheaderRu || '',
          paymentPageFormSubheaderKz: data.paymentPageFormSubheaderKz || '',
          paymentPageFormSubheaderEn: data.paymentPageFormSubheaderEn || '',
          paymentPageAmountCaptionRu: data.paymentPageAmountCaptionRu || '',
          paymentPageAmountCaptionKz: data.paymentPageAmountCaptionKz || '',
          paymentPageAmountCaptionEn: data.paymentPageAmountCaptionEn || '',
          paymentPageAmountHintRu: data.paymentPageAmountHintRu || '',
          paymentPageAmountHintKz: data.paymentPageAmountHintKz || '',
          paymentPageAmountHintEn: data.paymentPageAmountHintEn || '',
          paymentPageQuickSelectHeaderRu: data.paymentPageQuickSelectHeaderRu || '',
          paymentPageQuickSelectHeaderKz: data.paymentPageQuickSelectHeaderKz || '',
          paymentPageQuickSelectHeaderEn: data.paymentPageQuickSelectHeaderEn || '',
          paymentPageCommentCaptionRu: data.paymentPageCommentCaptionRu || '',
          paymentPageCommentCaptionKz: data.paymentPageCommentCaptionKz || '',
          paymentPageCommentCaptionEn: data.paymentPageCommentCaptionEn || '',
          paymentPageSubmitButtonRu: data.paymentPageSubmitButtonRu || '',
          paymentPageSubmitButtonKz: data.paymentPageSubmitButtonKz || '',
          paymentPageSubmitButtonEn: data.paymentPageSubmitButtonEn || '',
          paymentPageProcessingRu: data.paymentPageProcessingRu || '',
          paymentPageProcessingKz: data.paymentPageProcessingKz || '',
          paymentPageProcessingEn: data.paymentPageProcessingEn || '',
          paymentPageSuccessTitleRu: data.paymentPageSuccessTitleRu || '',
          paymentPageSuccessTitleKz: data.paymentPageSuccessTitleKz || '',
          paymentPageSuccessTitleEn: data.paymentPageSuccessTitleEn || '',
          paymentPageSuccessMessageRu: data.paymentPageSuccessMessageRu || '',
          paymentPageSuccessMessageKz: data.paymentPageSuccessMessageKz || '',
          paymentPageSuccessMessageEn: data.paymentPageSuccessMessageEn || '',
          paymentPageAmountToPayRu: data.paymentPageAmountToPayRu || '',
          paymentPageAmountToPayKz: data.paymentPageAmountToPayKz || '',
          paymentPageAmountToPayEn: data.paymentPageAmountToPayEn || '',
          paymentPageOriginalAmountRu: data.paymentPageOriginalAmountRu || '',
          paymentPageOriginalAmountKz: data.paymentPageOriginalAmountKz || '',
          paymentPageOriginalAmountEn: data.paymentPageOriginalAmountEn || '',
          paymentPageRequisitesRu: data.paymentPageRequisitesRu || '',
          paymentPageRequisitesKz: data.paymentPageRequisitesKz || '',
          paymentPageRequisitesEn: data.paymentPageRequisitesEn || '',
          paymentPageExpiresAtRu: data.paymentPageExpiresAtRu || '',
          paymentPageExpiresAtKz: data.paymentPageExpiresAtKz || '',
          paymentPageExpiresAtEn: data.paymentPageExpiresAtEn || '',
          paymentPageErrorRu: data.paymentPageErrorRu || '',
          paymentPageErrorKz: data.paymentPageErrorKz || '',
          paymentPageErrorEn: data.paymentPageErrorEn || '',
          // Новые поля для заголовков и карточек преимуществ
          paymentPageTitleRu: data.paymentPageTitleRu || '',
          paymentPageTitleKz: data.paymentPageTitleKz || '',
          paymentPageTitleEn: data.paymentPageTitleEn || '',
          paymentPageSubtitleRu: data.paymentPageSubtitleRu || '',
          paymentPageSubtitleKz: data.paymentPageSubtitleKz || '',
          paymentPageSubtitleEn: data.paymentPageSubtitleEn || '',
          paymentPageFeature1TitleRu: data.paymentPageFeature1TitleRu || '',
          paymentPageFeature1TitleKz: data.paymentPageFeature1TitleKz || '',
          paymentPageFeature1TitleEn: data.paymentPageFeature1TitleEn || '',
          paymentPageFeature1SubtitleRu: data.paymentPageFeature1SubtitleRu || '',
          paymentPageFeature1SubtitleKz: data.paymentPageFeature1SubtitleKz || '',
          paymentPageFeature1SubtitleEn: data.paymentPageFeature1SubtitleEn || '',
          paymentPageFeature1IconRu: data.paymentPageFeature1IconRu || '',
          paymentPageFeature1IconKz: data.paymentPageFeature1IconKz || '',
          paymentPageFeature1IconEn: data.paymentPageFeature1IconEn || '',
          paymentPageFeature2TitleRu: data.paymentPageFeature2TitleRu || '',
          paymentPageFeature2TitleKz: data.paymentPageFeature2TitleKz || '',
          paymentPageFeature2TitleEn: data.paymentPageFeature2TitleEn || '',
          paymentPageFeature2SubtitleRu: data.paymentPageFeature2SubtitleRu || '',
          paymentPageFeature2SubtitleKz: data.paymentPageFeature2SubtitleKz || '',
          paymentPageFeature2SubtitleEn: data.paymentPageFeature2SubtitleEn || '',
          paymentPageFeature2IconRu: data.paymentPageFeature2IconRu || '',
          paymentPageFeature2IconKz: data.paymentPageFeature2IconKz || '',
          paymentPageFeature2IconEn: data.paymentPageFeature2IconEn || '',
          paymentPageFeature3TitleRu: data.paymentPageFeature3TitleRu || '',
          paymentPageFeature3TitleKz: data.paymentPageFeature3TitleKz || '',
          paymentPageFeature3TitleEn: data.paymentPageFeature3TitleEn || '',
          paymentPageFeature3SubtitleRu: data.paymentPageFeature3SubtitleRu || '',
          paymentPageFeature3SubtitleKz: data.paymentPageFeature3SubtitleKz || '',
          paymentPageFeature3SubtitleEn: data.paymentPageFeature3SubtitleEn || '',
          paymentPageFeature3IconRu: data.paymentPageFeature3IconRu || '',
          paymentPageFeature3IconKz: data.paymentPageFeature3IconKz || '',
          paymentPageFeature3IconEn: data.paymentPageFeature3IconEn || '',
          // Simple design specific
          paymentPageSimpleSuccessRedirectSeconds: (typeof data.paymentPageSimpleSuccessRedirectSeconds === 'number' ? data.paymentPageSimpleSuccessRedirectSeconds : parseInt(data.paymentPageSimpleSuccessRedirectSeconds || '3', 10)) || 3,
          paymentPageSimpleReturnUrl: data.paymentPageSimpleReturnUrl || '',
  paymentPageNoUrlBehavior: (data as any).paymentPageNoUrlBehavior || 'none',
          paymentPageSimpleInstructionRu: data.paymentPageSimpleInstructionRu || '',
          paymentPageSimpleInstructionKz: data.paymentPageSimpleInstructionKz || '',
          paymentPageSimpleInstructionEn: data.paymentPageSimpleInstructionEn || '',
          paymentPageSimpleInstruction2Ru: data.paymentPageSimpleInstruction2Ru || '',
          paymentPageSimpleInstruction2Kz: data.paymentPageSimpleInstruction2Kz || '',
            paymentPageSimpleInstruction2En: data.paymentPageSimpleInstruction2En || '',
          // Simple UI visual toggles
          paymentPageSimpleAmountBorderEnabled: (data as any).paymentPageSimpleAmountBorderEnabled !== false,
          paymentPageSimpleStepNumberStyle: (((data as any).paymentPageSimpleStepNumberStyle as any) === 'roman') ? 'roman' : 'numeric',
          paymentPageSimpleStepNumberAsBadge: (data as any).paymentPageSimpleStepNumberAsBadge !== false,
          // Simple UI copy button
          paymentPageSimpleCopyButtonStyle: (((data as any).paymentPageSimpleCopyButtonStyle as any) === 'icon') ? 'icon' : 'text',
          paymentPageSimpleCopyButtonIcon: (data as any).paymentPageSimpleCopyButtonIcon || '📋',
          // Simple design colors per step and recipient
          paymentPageSimpleStep1Color: (data as any).paymentPageSimpleStep1Color || '#111827',
          paymentPageSimpleStep2Color: (data as any).paymentPageSimpleStep2Color || '#111827',
          paymentPageSimpleStep3Color: (data as any).paymentPageSimpleStep3Color || '#111827',
          paymentPageSimpleStep4Color: (data as any).paymentPageSimpleStep4Color || '#111827',
          paymentPageSimpleStep1NoteColor: (data as any).paymentPageSimpleStep1NoteColor || '#6B7280',
          paymentPageSimpleStep2NoteColor: (data as any).paymentPageSimpleStep2NoteColor || '#6B7280',
          paymentPageSimpleStep3NoteColor: (data as any).paymentPageSimpleStep3NoteColor || '#6B7280',
          paymentPageSimpleStep4NoteColor: (data as any).paymentPageSimpleStep4NoteColor || '#6B7280',
          paymentPageSimpleRecipientColor: (data as any).paymentPageSimpleRecipientColor || '#111827',
            paymentTtlMinutes: data.paymentTtlMinutes ?? 10,
            paymentAmountDivisor: (data as any).paymentAmountDivisor ? Number((data as any).paymentAmountDivisor) : 1,
            withdrawAmountDivisor: (data as any).withdrawAmountDivisor ? Number((data as any).withdrawAmountDivisor) : 1,
            paymentPageConfirmButtonUrl: (data as any).paymentPageConfirmButtonUrl || '',
            paymentPageSimpleStep1TitleRu: data.paymentPageSimpleStep1TitleRu || '',
            paymentPageSimpleStep1TitleKz: data.paymentPageSimpleStep1TitleKz || '',
            paymentPageSimpleStep1TitleEn: data.paymentPageSimpleStep1TitleEn || '',
            paymentPageSimpleStep1NoteRu: data.paymentPageSimpleStep1NoteRu || '',
            paymentPageSimpleStep1NoteKz: data.paymentPageSimpleStep1NoteKz || '',
            paymentPageSimpleStep1NoteEn: data.paymentPageSimpleStep1NoteEn || '',
            paymentPageSimpleStep2TitleRu: data.paymentPageSimpleStep2TitleRu || '',
            paymentPageSimpleStep2TitleKz: data.paymentPageSimpleStep2TitleKz || '',
            paymentPageSimpleStep2TitleEn: data.paymentPageSimpleStep2TitleEn || '',
            paymentPageSimpleStep2NoteRu: data.paymentPageSimpleStep2NoteRu || '',
            paymentPageSimpleStep2NoteKz: data.paymentPageSimpleStep2NoteKz || '',
            paymentPageSimpleStep2NoteEn: data.paymentPageSimpleStep2NoteEn || '',
            paymentPageSimpleStep3TitleRu: data.paymentPageSimpleStep3TitleRu || '',
            paymentPageSimpleStep3TitleKz: data.paymentPageSimpleStep3TitleKz || '',
            paymentPageSimpleStep3TitleEn: data.paymentPageSimpleStep3TitleEn || '',
            paymentPageSimpleStep3NoteRu: data.paymentPageSimpleStep3NoteRu || '',
            paymentPageSimpleStep3NoteKz: data.paymentPageSimpleStep3NoteKz || '',
            paymentPageSimpleStep3NoteEn: data.paymentPageSimpleStep3NoteEn || '',
            paymentPageSimpleStep4TitleRu: data.paymentPageSimpleStep4TitleRu || '',
            paymentPageSimpleStep4TitleKz: data.paymentPageSimpleStep4TitleKz || '',
            paymentPageSimpleStep4TitleEn: data.paymentPageSimpleStep4TitleEn || '',
            paymentPageSimpleStep4NoteRu: data.paymentPageSimpleStep4NoteRu || '',
            paymentPageSimpleStep4NoteKz: data.paymentPageSimpleStep4NoteKz || '',
            paymentPageSimpleStep4NoteEn: data.paymentPageSimpleStep4NoteEn || '',
            // Simple design modals customization
            paymentPageSimpleSuccessBgColor: (data as any).paymentPageSimpleSuccessBgColor || '#ECFDF5',
            paymentPageSimpleSuccessIcon: (data as any).paymentPageSimpleSuccessIcon || '✅',
            paymentPageSimpleSuccessTitleRu: (data as any).paymentPageSimpleSuccessTitleRu || 'Пополнено',
            paymentPageSimpleSuccessTitleKz: (data as any).paymentPageSimpleSuccessTitleKz || 'Пополнение сәтті',
            paymentPageSimpleSuccessTitleEn: (data as any).paymentPageSimpleSuccessTitleEn || 'Top-up successful',
            paymentPageSimpleSuccessMessageRu: (data as any).paymentPageSimpleSuccessMessageRu || 'Сейчас вернём вас в магазин',
            paymentPageSimpleSuccessMessageKz: (data as any).paymentPageSimpleSuccessMessageKz || 'Жақында дүкенге қайта бағыттаймыз',
            paymentPageSimpleSuccessMessageEn: (data as any).paymentPageSimpleSuccessMessageEn || 'You will be redirected to the shop shortly',
            paymentPageSimpleFailureBgColor: (data as any).paymentPageSimpleFailureBgColor || '#FEF2F2',
            paymentPageSimpleFailureIcon: (data as any).paymentPageSimpleFailureIcon || '❌',
            paymentPageSimpleFailureTitleRu: (data as any).paymentPageSimpleFailureTitleRu || 'Оплата не прошла',
            paymentPageSimpleFailureTitleKz: (data as any).paymentPageSimpleFailureTitleKz || 'Төлем өтпеді',
            paymentPageSimpleFailureTitleEn: (data as any).paymentPageSimpleFailureTitleEn || 'Payment failed',
            paymentPageSimpleFailureMessageRu: (data as any).paymentPageSimpleFailureMessageRu || 'Попробуйте ещё раз или вернитесь в магазин',
            paymentPageSimpleFailureMessageKz: (data as any).paymentPageSimpleFailureMessageKz || 'Қайтадан көріңіз немесе дүкенге оралыңыз',
            paymentPageSimpleFailureMessageEn: (data as any).paymentPageSimpleFailureMessageEn || 'Try again or return to the shop',
            // Alt логотипа
            paymentPageLogoAltRu: (data as any).paymentPageLogoAltRu || '',
            paymentPageLogoAltKz: (data as any).paymentPageLogoAltKz || '',
            paymentPageLogoAltEn: (data as any).paymentPageLogoAltEn || '',
            
            // ===== Настройки страницы вывода средств =====
            withdrawPageEnabled: (data as any).withdrawPageEnabled !== false,
            withdrawPageDesign: (data as any).withdrawPageDesign || 'simple',
            withdrawPageTitle: (data as any).withdrawPageTitle || '',
            withdrawPageSubtitle: (data as any).withdrawPageSubtitle || '',
            withdrawPagePrimaryColor: (data as any).withdrawPagePrimaryColor || '#10B981',
            withdrawPageButtonColor: (data as any).withdrawPageButtonColor || '#10B981',
            withdrawPageButtonTextColor: (data as any).withdrawPageButtonTextColor || '#FFFFFF',
            withdrawPageBackgroundColor: (data as any).withdrawPageBackgroundColor || '',
            withdrawPageAccentColor: (data as any).withdrawPageAccentColor || '#059669',
            withdrawPageCustomCSS: (data as any).withdrawPageCustomCSS || '',
            withdrawPageCustomJS: (data as any).withdrawPageCustomJS || '',
            withdrawPageTextColor: (data as any).withdrawPageTextColor || '#1F2937',
            withdrawPageAmountTextColor: (data as any).withdrawPageAmountTextColor || '#2563EB',
            withdrawPageLogoEnabled: (data as any).withdrawPageLogoEnabled !== false,
            withdrawPageFooterEnabled: (data as any).withdrawPageFooterEnabled !== false,
            // Новые цвета карточки
            withdrawPageCardBg: (data as any).withdrawPageCardBg || '#FFFFFF',
            withdrawPageBorderColor: (data as any).withdrawPageBorderColor || ((data as any).withdrawPagePrimaryColor || '#10B981'),
            
            // Мультиязычные заголовки
            withdrawPageTitleRu: (data as any).withdrawPageTitleRu || '',
            withdrawPageTitleKz: (data as any).withdrawPageTitleKz || '',
            withdrawPageTitleEn: (data as any).withdrawPageTitleEn || '',
            withdrawPageSubtitleRu: (data as any).withdrawPageSubtitleRu || '',
            withdrawPageSubtitleKz: (data as any).withdrawPageSubtitleKz || '',
            withdrawPageSubtitleEn: (data as any).withdrawPageSubtitleEn || '',
            
            // Заголовки формы
            withdrawPageFormHeaderRu: (data as any).withdrawPageFormHeaderRu || '',
            withdrawPageFormHeaderKz: (data as any).withdrawPageFormHeaderKz || '',
            withdrawPageFormHeaderEn: (data as any).withdrawPageFormHeaderEn || '',
            withdrawPageFormSubheaderRu: (data as any).withdrawPageFormSubheaderRu || '',
            withdrawPageFormSubheaderKz: (data as any).withdrawPageFormSubheaderKz || '',
            withdrawPageFormSubheaderEn: (data as any).withdrawPageFormSubheaderEn || '',
            
            // Подписи полей
            withdrawPageAmountCaptionRu: (data as any).withdrawPageAmountCaptionRu || '',
            withdrawPageAmountCaptionKz: (data as any).withdrawPageAmountCaptionKz || '',
            withdrawPageAmountCaptionEn: (data as any).withdrawPageAmountCaptionEn || '',
            withdrawPageCardCaptionRu: (data as any).withdrawPageCardCaptionRu || '',
            withdrawPageCardCaptionKz: (data as any).withdrawPageCardCaptionKz || '',
            withdrawPageCardCaptionEn: (data as any).withdrawPageCardCaptionEn || '',
            withdrawPageNameCaptionRu: (data as any).withdrawPageNameCaptionRu || '',
            withdrawPageNameCaptionKz: (data as any).withdrawPageNameCaptionKz || '',
            withdrawPageNameCaptionEn: (data as any).withdrawPageNameCaptionEn || '',
            
            // Кнопки и статусы
            withdrawPageSubmitButtonRu: (data as any).withdrawPageSubmitButtonRu || '',
            withdrawPageSubmitButtonKz: (data as any).withdrawPageSubmitButtonKz || '',
            withdrawPageSubmitButtonEn: (data as any).withdrawPageSubmitButtonEn || '',
            withdrawPageEditButtonRu: (data as any).withdrawPageEditButtonRu || '',
            withdrawPageEditButtonKz: (data as any).withdrawPageEditButtonKz || '',
            withdrawPageEditButtonEn: (data as any).withdrawPageEditButtonEn || '',
            withdrawPageProcessingRu: (data as any).withdrawPageProcessingRu || '',
            withdrawPageProcessingKz: (data as any).withdrawPageProcessingKz || '',
            withdrawPageProcessingEn: (data as any).withdrawPageProcessingEn || '',
            withdrawPageSuccessTitleRu: (data as any).withdrawPageSuccessTitleRu || '',
            withdrawPageSuccessTitleKz: (data as any).withdrawPageSuccessTitleKz || '',
            withdrawPageSuccessTitleEn: (data as any).withdrawPageSuccessTitleEn || '',
            withdrawPageSuccessMessageRu: (data as any).withdrawPageSuccessMessageRu || '',
            withdrawPageSuccessMessageKz: (data as any).withdrawPageSuccessMessageKz || '',
            withdrawPageSuccessMessageEn: (data as any).withdrawPageSuccessMessageEn || '',
            withdrawPageErrorRu: (data as any).withdrawPageErrorRu || '',
            withdrawPageErrorKz: (data as any).withdrawPageErrorKz || '',
            withdrawPageErrorEn: (data as any).withdrawPageErrorEn || '',
            
            // Сообщения для пользователей
            withdrawPageExistingUserMessageRu: (data as any).withdrawPageExistingUserMessageRu || '',
            withdrawPageExistingUserMessageKz: (data as any).withdrawPageExistingUserMessageKz || '',
            withdrawPageExistingUserMessageEn: (data as any).withdrawPageExistingUserMessageEn || '',
            withdrawPageNewUserMessageRu: (data as any).withdrawPageNewUserMessageRu || '',
            withdrawPageNewUserMessageKz: (data as any).withdrawPageNewUserMessageKz || '',
            withdrawPageNewUserMessageEn: (data as any).withdrawPageNewUserMessageEn || '',
            
            // Настройки безопасности
            withdrawPageAllowEdit: (data as any).withdrawPageAllowEdit !== false,
            withdrawPageRequireConfirmation: (data as any).withdrawPageRequireConfirmation !== false,
            
            // Плитки преимуществ
            withdrawPageFeature1TitleRu: (data as any).withdrawPageFeature1TitleRu || '',
            withdrawPageFeature1TitleKz: (data as any).withdrawPageFeature1TitleKz || '',
            withdrawPageFeature1TitleEn: (data as any).withdrawPageFeature1TitleEn || '',
            withdrawPageFeature1SubtitleRu: (data as any).withdrawPageFeature1SubtitleRu || '',
            withdrawPageFeature1SubtitleKz: (data as any).withdrawPageFeature1SubtitleKz || '',
            withdrawPageFeature1SubtitleEn: (data as any).withdrawPageFeature1SubtitleEn || '',
            withdrawPageFeature1IconRu: (data as any).withdrawPageFeature1IconRu || '',
            withdrawPageFeature1IconKz: (data as any).withdrawPageFeature1IconKz || '',
            withdrawPageFeature1IconEn: (data as any).withdrawPageFeature1IconEn || '',
            withdrawPageFeature2TitleRu: (data as any).withdrawPageFeature2TitleRu || '',
            withdrawPageFeature2TitleKz: (data as any).withdrawPageFeature2TitleKz || '',
            withdrawPageFeature2TitleEn: (data as any).withdrawPageFeature2TitleEn || '',
            withdrawPageFeature2SubtitleRu: (data as any).withdrawPageFeature2SubtitleRu || '',
            withdrawPageFeature2SubtitleKz: (data as any).withdrawPageFeature2SubtitleKz || '',
            withdrawPageFeature2SubtitleEn: (data as any).withdrawPageFeature2SubtitleEn || '',
            withdrawPageFeature2IconRu: (data as any).withdrawPageFeature2IconRu || '',
            withdrawPageFeature2IconKz: (data as any).withdrawPageFeature2IconKz || '',
            withdrawPageFeature2IconEn: (data as any).withdrawPageFeature2IconEn || '',
            withdrawPageFeature3TitleRu: (data as any).withdrawPageFeature3TitleRu || '',
            withdrawPageFeature3TitleKz: (data as any).withdrawPageFeature3TitleKz || '',
            withdrawPageFeature3TitleEn: (data as any).withdrawPageFeature3TitleEn || '',
            withdrawPageFeature3SubtitleRu: (data as any).withdrawPageFeature3SubtitleRu || '',
            withdrawPageFeature3SubtitleKz: (data as any).withdrawPageFeature3SubtitleKz || '',
            withdrawPageFeature3SubtitleEn: (data as any).withdrawPageFeature3SubtitleEn || '',
            withdrawPageFeature3IconRu: (data as any).withdrawPageFeature3IconRu || '',
            withdrawPageFeature3IconKz: (data as any).withdrawPageFeature3IconKz || '',
            withdrawPageFeature3IconEn: (data as any).withdrawPageFeature3IconEn || '',
            // Callbacks defaults
            includeTransactionIdInCallbacks: false,
            includeBuyerPaidInCallbacks: false,
            includePaymentIdInCallbacks: false,
        };
        setSettings(newSettings as any);
      }
    } catch (error) {
      console.error('Error fetching shop settings:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  // Функция для принудительного обновления настроек
  const refreshSettings = useCallback(async () => {
    if (user?.role !== 'admin') return;
    
    try {
      setLoading(true);
      await fetchShopSettings();
      alert('Настройки обновлены!');
    } catch (error) {
      console.error('Error refreshing settings:', error);
      alert('Ошибка при обновлении настроек');
    } finally {
      setLoading(false);
    }
  }, [user?.role, fetchShopSettings]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchShopSettings();
    }
  }, [user?.role, fetchShopSettings]);

  const handleSaveSettings = useCallback(async () => {
    setSaving(true);
    try {
      // Проверяем типы данных перед отправкой
      console.log('Settings object:', settings);
      console.log('Settings types:', Object.fromEntries(
        Object.entries(settings).map(([key, value]) => [key, typeof value])
      ));
      
      // Убеждаемся, что все поля имеют правильные типы
      const sanitizedSettings = { ...settings };
      
      // Преобразуем boolean поля
      if (typeof sanitizedSettings.autoConfirmEnabled === 'string') {
        sanitizedSettings.autoConfirmEnabled = sanitizedSettings.autoConfirmEnabled === 'true';
      }
      if (typeof sanitizedSettings.paymentPageShowAmount === 'string') {
        sanitizedSettings.paymentPageShowAmount = sanitizedSettings.paymentPageShowAmount === 'true';
      }
      if (typeof sanitizedSettings.paymentPageShowCurrency === 'string') {
        sanitizedSettings.paymentPageShowCurrency = sanitizedSettings.paymentPageShowCurrency === 'true';
      }
      if (typeof sanitizedSettings.paymentPageFeaturesEnabled === 'string') {
        sanitizedSettings.paymentPageFeaturesEnabled = sanitizedSettings.paymentPageFeaturesEnabled === 'true';
      }
      if (typeof sanitizedSettings.paymentPageCustomAmountEnabled === 'string') {
        sanitizedSettings.paymentPageCustomAmountEnabled = sanitizedSettings.paymentPageCustomAmountEnabled === 'true';
      }
      if (typeof sanitizedSettings.paymentPageLogoEnabled === 'string') {
        sanitizedSettings.paymentPageLogoEnabled = sanitizedSettings.paymentPageLogoEnabled === 'true';
      }
      if (typeof (sanitizedSettings as any).paymentPageHeaderEnabled === 'string') {
        (sanitizedSettings as any).paymentPageHeaderEnabled = (sanitizedSettings as any).paymentPageHeaderEnabled === 'true';
      }
      if (typeof (sanitizedSettings as any).paymentPageFooterEnabled === 'string') {
        (sanitizedSettings as any).paymentPageFooterEnabled = (sanitizedSettings as any).paymentPageFooterEnabled === 'true';
      }
      if (typeof (sanitizedSettings as any).paymentPublicIdEnabled === 'string') {
        (sanitizedSettings as any).paymentPublicIdEnabled = (sanitizedSettings as any).paymentPublicIdEnabled === 'true';
      }
      if (typeof (sanitizedSettings as any).withdrawPageLogoEnabled === 'string') {
        (sanitizedSettings as any).withdrawPageLogoEnabled = (sanitizedSettings as any).withdrawPageLogoEnabled === 'true';
      }
      if (typeof (sanitizedSettings as any).withdrawPageAllowEdit === 'string') {
        (sanitizedSettings as any).withdrawPageAllowEdit = (sanitizedSettings as any).withdrawPageAllowEdit === 'true';
      }
      if (typeof (sanitizedSettings as any).withdrawPageRequireConfirmation === 'string') {
        (sanitizedSettings as any).withdrawPageRequireConfirmation = (sanitizedSettings as any).withdrawPageRequireConfirmation === 'true';
      }
      
      // Преобразуем числовые поля
      if (typeof sanitizedSettings.paymentPageDefaultAmount === 'string' && sanitizedSettings.paymentPageDefaultAmount !== '') {
        sanitizedSettings.paymentPageDefaultAmount = parseFloat(sanitizedSettings.paymentPageDefaultAmount);
      }
      if (typeof sanitizedSettings.paymentPageMinAmount === 'string' && sanitizedSettings.paymentPageMinAmount !== '') {
        sanitizedSettings.paymentPageMinAmount = parseFloat(sanitizedSettings.paymentPageMinAmount);
      }
      if (typeof (sanitizedSettings as any).paymentTtlMinutes === 'string' && (sanitizedSettings as any).paymentTtlMinutes !== '') {
        (sanitizedSettings as any).paymentTtlMinutes = parseInt((sanitizedSettings as any).paymentTtlMinutes, 10);
      }
      if ((sanitizedSettings as any).paymentAmountDivisor !== undefined) {
        const val = (sanitizedSettings as any).paymentAmountDivisor;
        (sanitizedSettings as any).paymentAmountDivisor = Math.max(1, typeof val === 'string' ? parseInt(val, 10) : Number(val));
      }
      if ((sanitizedSettings as any).withdrawAmountDivisor !== undefined) {
        const val = (sanitizedSettings as any).withdrawAmountDivisor;
        (sanitizedSettings as any).withdrawAmountDivisor = Math.max(1, typeof val === 'string' ? parseInt(val, 10) : Number(val));
      }
      if (typeof (sanitizedSettings as any).paymentPageSimpleSuccessRedirectSeconds === 'string' && (sanitizedSettings as any).paymentPageSimpleSuccessRedirectSeconds !== '') {
        (sanitizedSettings as any).paymentPageSimpleSuccessRedirectSeconds = parseInt((sanitizedSettings as any).paymentPageSimpleSuccessRedirectSeconds, 10);
      }
      
      console.log('Sanitized settings data:', JSON.stringify(sanitizedSettings, null, 2));
      
      const response = await fetch('/api/admin/shop/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(sanitizedSettings)
      });

      if (response.ok) {
        alert('Настройки успешно сохранены');
        // Обновляем настройки после сохранения
        await fetchShopSettings();
      } else {
        const errorData = await response.json();
        console.error('Server error response:', errorData);
        
        // Форматируем детали ошибки
        let detailsText = 'Нет деталей';
        if (errorData.details) {
          if (typeof errorData.details === 'object') {
            detailsText = JSON.stringify(errorData.details, null, 2);
          } else {
            detailsText = String(errorData.details);
          }
        }
        
        // Форматируем полученные данные
        let receivedDataText = 'Нет данных';
        if (errorData.receivedData) {
          receivedDataText = JSON.stringify(errorData.receivedData, null, 2);
        }
        
        alert(`Ошибка при сохранении: ${errorData.error || 'Неизвестная ошибка'}\n\nДетали: ${detailsText}\n\nПолученные данные: ${receivedDataText}`);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Ошибка при сохранении настроек');
    } finally {
      setSaving(false);
    }
  }, [settings, fetchShopSettings]);

  const handleRotateApiKey = useCallback(async () => {
    if (!confirm('Вы уверены, что хотите изменить API ключ? Это может нарушить работу интеграций.')) {
      return;
    }

    try {
      const response = await fetch('/api/admin/shop/rotate-api-key', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNewApiKey(data.newApiKey);
        setShowApiKeyModal(true);
        fetchShopSettings();
      } else {
        const errorData = await response.json();
        alert(`Ошибка: ${errorData.error || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      console.error('Error rotating API key:', error);
      alert('Ошибка при изменении API ключа');
    }
  }, [fetchShopSettings]);

  const handleTestWebhook = async () => {
    if (!webhookTestUrl) {
      alert('Введите URL для тестирования');
      return;
    }

    try {
      const response = await fetch('/api/admin/shop/test-webhook', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ webhookUrl: webhookTestUrl })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.webhookTested) {
          setWebhookTestResult(`✅ Успешно! Статус: ${data.statusCode}`);
        } else {
          setWebhookTestResult(`❌ Ошибка: ${data.error || 'Неизвестная ошибка'}`);
        }
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      setWebhookTestResult('❌ Ошибка при тестировании');
    }
  };

  const getBaseUrl = () => {
    if (settings.domain) {
      // Используем HTTPS для пользовательского домена
      return `https://${settings.domain}`;
    }
    // Fallback на localhost для разработки
    return window.location.origin.replace(':5002', ':5001');
  };

  const previewPaymentPage = () => {
    const baseUrl = getBaseUrl();
    const previewUrl = `${baseUrl}?preview=true&shop=${shopInfo?.id}`;
    window.open(previewUrl, '_blank');
  };

  if (user?.role !== 'admin') {
    return <div className="text-center text-red-600">Доступ запрещен</div>;
  }

  if (loading) {
    return <div className="text-center">Загрузка...</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold text-slate-900">Настройки магазина</h2>
          <p className="text-sm text-slate-500">Управление настройками магазина, API ключами, веб-хуками и реквизитами</p>
        </div>
        <FiltersTabs tabs={tabItems} value={activeTab} onChange={(key) => setActiveTab(key as TabKey)} />
      </div>

      <div className="space-y-6">
        {/* Информация о магазине - всегда видна */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Информация о магазине</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Название магазина</label>
              <p className="mt-1 text-sm text-gray-900">{shopInfo?.name}</p>
              <button
                onClick={previewPaymentPage}
                className="mt-2 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 text-sm"
              >
                Предварительный просмотр
              </button>
              <button
                onClick={() => {
                  const baseUrl = getBaseUrl();
                  const previewUrl = `${baseUrl}/withdraw/preview?shop=${shopInfo?.id}`;
                  window.open(previewUrl, '_blank');
                }}
                className="mt-2 ml-2 bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm"
              >
                Предпросмотр вывода
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Текущий API ключ</label>
              <div className="mt-1 flex items-center space-x-2">
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                  {shopInfo?.apiKey ? `${shopInfo.apiKey.substring(0, 20)}...` : 'Не установлен'}
                </code>
                <button
                  onClick={handleRotateApiKey}
                  className="bg-yellow-600 text-white px-3 py-2 rounded-md hover:bg-yellow-700 text-sm"
                >
                  Сменить
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Основные настройки */}
        {activeTab === 'general' && (
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Основные настройки</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Валюта</label>
                <select
                  value={settings.currency || 'RUB'}
                  onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  {availableCurrencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Домен</label>
                <input
                  type="text"
                  value={settings.domain || ''}
                  onChange={(e) => setSettings({ ...settings, domain: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="example.com"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Укажите ваш домен без протокола (без http:// или https://). 
                  <br />
                  <strong>Важно:</strong> После изменения домена все ссылки в разделе "Платежная страница" будут автоматически обновлены.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Делитель суммы вывода</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={(settings as any).withdrawAmountDivisor || 1}
                  onChange={(e) => setSettings({ ...settings, withdrawAmountDivisor: Math.max(1, parseInt(e.target.value || '1') || 1) as any })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Применяется к сумме вывода, которую отправляет магазин; делится перед созданием заявки.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Делитель суммы магазина</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={settings.paymentAmountDivisor || 1}
                  onChange={(e) => setSettings({ ...settings, paymentAmountDivisor: Math.max(1, parseInt(e.target.value || '1') || 1) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Применяется к суммам, которые отправляет магазин; сумма делится перед резервированием.
                </p>
              </div>

              {/* Удалена настройка темы оформления (light/dark/auto) как неиспользуемая */}

              <div>
                <label className="block text-sm font-medium text-gray-700">Дизайн платежной страницы</label>
                <select
                  value={settings.paymentPageDesign || 'simple'}
                  onChange={(e) => setSettings({ ...settings, paymentPageDesign: e.target.value as any })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="simple">Простой (v2)</option>
                  <option value="classic">Классический (v1)</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">Выбор дизайна влияет на доступные настройки вкладок.</p>
              </div>

              {/* Публичный код платежа (перенесено в Общие) */}
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="publicIdEnabledGeneral"
                  checked={!!settings.paymentPublicIdEnabled}
                  onChange={(e) => setSettings({ ...settings, paymentPublicIdEnabled: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="publicIdEnabledGeneral" className="ml-2 block text-sm text-gray-900">
                  Использовать публичный код платежа (защита ссылок)
                </label>
              </div>
            </div>

            {/* Автоматическое подтверждение */}
            <div className="mt-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoConfirm"
                  checked={settings.autoConfirmEnabled || false}
                  onChange={(e) => setSettings({ ...settings, autoConfirmEnabled: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="autoConfirm" className="ml-2 block text-sm text-gray-900">
                  Автоподтверждение успешных платежей
                </label>
              </div>
              <p className="mt-1 text-sm text-gray-500">Когда включено — успешные платежи подтверждаются автоматически.</p>
            </div>
            {/* Безопасность веб-страниц (перенесено из вывода) */}
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-900 mb-2">🔒 Безопасность веб-страниц</h4>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!settings.withdrawPageAllowEdit}
                    onChange={(e) => setSettings({ ...settings, withdrawPageAllowEdit: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Разрешить редактирование реквизитов в рамках запроса вывода</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!settings.withdrawPageRequireConfirmation}
                    onChange={(e) => setSettings({ ...settings, withdrawPageRequireConfirmation: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Требовать подтверждение оператором (вывод)</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Simple UI настройки (deprecated tab removed) */}
        {false && (
          <div className="card p-6">
            {/* deprecated simple tab content removed */}
          </div>
        )}
        {false && (
          <div className="card p-6">
            {/* deprecated payment-page tab content removed */}
          </div>
        )}

        {/* Настройки веб-хуков */}
        {activeTab === 'integrations' && (
          <div className="space-y-6">
            {/* Веб-хуки */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">🔔 Веб-хуки</h3>
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">URL веб-хука</label>
                <input
                  type="url"
                  value={settings.webhookUrl || ''}
                  onChange={(e) => setSettings({ ...settings, webhookUrl: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="https://your-domain.com/webhook"
                />
                <p className="mt-1 text-sm text-gray-500">
                  URL для получения уведомлений о платежах
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Разрешенные IP адреса</label>
                <input
                  type="text"
                  value={settings.ipAllowlist || ''}
                  onChange={(e) => setSettings({ ...settings, ipAllowlist: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="192.168.1.1, 10.0.0.0/8"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Список IP адресов, с которых разрешены запросы (через запятую)
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Включать external_trx в вебхуки</label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={!!settings.includeTransactionIdInCallbacks}
                      onChange={async (e) => {
                        const newVal = e.target.checked;
                        setSettings((s: any) => ({ ...s, includeTransactionIdInCallbacks: newVal }));
                        await fetch(`/api/owner/shops/${user?.shopId}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          },
                          body: JSON.stringify({ includeTransactionIdInCallbacks: newVal })
                        });
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Включать buyer_paid в вебхуки</label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={!!(settings.includeBuyerPaidInCallbacks || settings.include_buyer_paid_in_callbacks)}
                      onChange={async (e) => {
                        const newVal = e.target.checked;
                        setSettings((s: any) => ({ ...s, includeBuyerPaidInCallbacks: newVal }));
                        await fetch(`/api/owner/shops/${user?.shopId}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          },
                          body: JSON.stringify({ includeBuyerPaidInCallbacks: newVal })
                        });
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Включать ID транзакции покупателя в вебхуки</label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={!!settings.includePaymentIdInCallbacks}
                      onChange={async (e) => {
                        const newVal = e.target.checked;
                        setSettings((s: any) => ({ ...s, includePaymentIdInCallbacks: newVal }));
                        await fetch(`/api/owner/shops/${user?.shopId}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          },
                          body: JSON.stringify({ includePaymentIdInCallbacks: newVal })
                        });
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  onClick={handleTestWebhook}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Тестировать веб-хук
                </button>
                <div className="flex-1">
                  <input
                    type="url"
                    value={webhookTestUrl}
                    onChange={(e) => setWebhookTestUrl(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="URL для тестирования"
                  />
                </div>

                {/* Кнопка предпросмотра страницы вывода */}
                <div className="md:col-span-2">
                  <button
                    type="button"
                    onClick={() => {
                      const base = getBaseUrl();
                      const url = `${base}/withdraw/preview?shop=${shopInfo?.id || ''}`;
                      window.open(url, '_blank');
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Предпросмотр страницы вывода
                  </button>
                </div>
              </div>
              
              {webhookTestResult && (
                <div className={`p-3 rounded-md ${
                  webhookTestResult.includes('успешно') 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {webhookTestResult}
                </div>
              )}
            </div>
          </div>
          
          {/* Доступ к платежной странице */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">💻 Платежная страница</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Прямая ссылка на платежную страницу</label>
                  <div className="mt-1 flex items-center space-x-2">
                    <input
                      type="text"
                      value={`${getBaseUrl()}?shop=${shopInfo?.id}`}
                      readOnly
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono text-sm"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(`${getBaseUrl()}?shop=${shopInfo?.id}`)}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      Копировать
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Используйте эту ссылку для прямого доступа к платежной странице вашего магазина
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Код для вставки на ваш сайт</label>
                  <textarea
                    value={`<iframe src="${getBaseUrl()}?shop=${shopInfo?.id}&iframe=1" width="100%" height="600" frameborder="0"></iframe>`}
                    readOnly
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono text-sm"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Скопируйте этот код и вставьте на ваш сайт для интеграции платежной страницы
                  </p>
                </div>
                
              </div>
            </div>
            {/* Доступ к странице вывода средств */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">🏧 Страница вывода средств</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ссылка на предпросмотр страницы вывода</label>
                  <div className="mt-1 flex items-center space-x-2">
                    <input
                      type="text"
                      value={`${getBaseUrl()}/withdraw/preview?shop=${shopInfo?.id}`}
                      readOnly
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono text-sm"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(`${getBaseUrl()}/withdraw/preview?shop=${shopInfo?.id}`)}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      Копировать
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Это только предпросмотр дизайна и текстов. Для реального вывода используется индивидуальная ссылка с publicId (см. ниже).
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Код для вставки (предпросмотр)</label>
                  <textarea
                    value={`<iframe src="${getBaseUrl()}/withdraw/preview?shop=${shopInfo?.id}&iframe=1" width="100%" height="640" frameborder="0"></iframe>`}
                    readOnly
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono text-sm"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Предпросмотр для интеграции. Рабочий iframe ниже — замените {`{publicId}`} на значение из API.
                  </p>
                </div>

                {/* Рабочая ссылка и iframe-шаблон */}
                <div className="pt-2 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700">Рабочая ссылка (после инициализации)</label>
                  <div className="mt-1 flex items-center space-x-2">
                    <input
                      type="text"
                      value={`${getBaseUrl()}/withdraw/${'{publicId}'}`}
                      readOnly
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono text-sm"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(`${getBaseUrl()}/withdraw/${'{publicId}'}`)}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      Копировать
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Получите {`{publicId}`} из ответа API инициализации вывода.
                  </p>

                  <label className="block text-sm font-medium text-gray-700 mt-4">Шаблон iframe (рабочий)</label>
                  <textarea
                    value={`<iframe src="${getBaseUrl()}/withdraw/${'{publicId}'}?iframe=1" width="100%" height="640" frameborder="0"></iframe>`}
                    readOnly
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 font-mono text-sm"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Замените {`{publicId}`} из ответа <code className="font-mono">POST {getBaseUrl()}/api/withdrawal/init</code>.
                  </p>

                  <div className="bg-gray-50 p-3 rounded-md border mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Пример запроса инициализации:</p>
                    <code className="text-xs text-gray-700 block bg-white p-2 rounded border whitespace-pre">{`curl -X POST '${getBaseUrl()}/api/withdrawal/init' \\\n  -H 'Content-Type: application/json' \\\n  -H 'X-Shop-Id: ${shopInfo?.id || ''}' \\\n  -d '{"username":"demo_user","amount":1000,"currency":"RUB"}'`}</code>
                    <p className="text-xs text-gray-500 mt-2">В ответе вернётся поле <code className="font-mono">url</code>, например <code className="font-mono">/withdraw/wdr_xxx</code>.</p>
                  </div>
                </div>
              </div>
            </div>
            {/* API для интеграции */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">🔗 API для интеграции</h3>
              <div className="space-y-3">
                <div className="bg-gray-50 p-4 rounded-md border">
                  <p className="text-sm font-medium text-gray-700 mb-2">Инициализация платежа:</p>
                  <code className="text-xs text-gray-600 block bg-white p-2 rounded border">
                    POST {getBaseUrl()}/api/payments/init?shop={shopInfo?.id}
                  </code>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border">
                  <p className="text-sm font-medium text-gray-700 mb-2">Подтверждение платежа:</p>
                  <code className="text-xs text-gray-600 block bg-white p-2 rounded border">
                    POST {getBaseUrl()}/api/payments/confirm
                  </code>
                </div>
                <div className="bg-gray-50 p-4 rounded-md border">
                  <p className="text-sm font-medium text-gray-700 mb-2">Проверка статуса:</p>
                  <code className="text-xs text-gray-600 block bg-white p-2 rounded border">
                    GET {getBaseUrl()}/api/payments/{'{paymentId}'}/status?shop={shopInfo?.id}
                  </code>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Устаревший API:</strong> Рекомендуем использовать Shop1 API (ниже) для новых интеграций.
                  </p>
                </div>
              </div>
            </div>

            {/* Shop1 API Integration */}
            <div className="card p-6">
              <div className="flex items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">🚀 Shop1 API (рекомендуемый)</h3>
                <span className="ml-3 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                  Соответствует ShopMetod.md
                </span>
              </div>
              
              {/* Enable/Disable Toggle */}
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.shop1Enabled || false}
                    onChange={(e) => setSettings({ ...settings, shop1Enabled: e.target.checked })}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900">
                    Включить Shop1 API интеграцию
                  </span>
                </label>
                <p className="mt-2 text-sm text-gray-600">
                  Предоставляет endpoints: /api/shop1/create-request, /api/shop1/create-withdrawal, /api/shop1/withdrawal-status
                </p>
              </div>

              {settings.shop1Enabled && (
                <div className="space-y-6">
                  {/* Security Section */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      🔐 Безопасность и аутентификация
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        HMAC Password для подписи запросов *
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="password"
                          value={settings.shop1HmacPassword || ''}
                          onChange={(e) => setSettings({ ...settings, shop1HmacPassword: e.target.value })}
                          className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                          placeholder={settings.shop1HmacPasswordSet ? '••••••••' : 'Введите пароль минимум 8 символов'}
                          minLength={8}
                          required
                        />
                        {settings.shop1HmacPasswordSet && (
                          <span className="text-green-600 text-sm font-medium">✓ Установлен</span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Используется для подписи всех запросов и ответов HMAC SHA512
                      </p>
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Разрешенные IP адреса (опционально)
                      </label>
                      <input
                        type="text"
                        value={settings.shop1AllowedIps || ''}
                        onChange={(e) => setSettings({ ...settings, shop1AllowedIps: e.target.value })}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="192.168.1.1, 10.0.0.0/24, 203.0.113.0/24"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        IP адреса через запятую. Если пусто - разрешены запросы с любых IP
                      </p>
                    </div>
                  </div>

                  {/* Default URLs Section */}
                  <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      🔗 Шаблоны URL для редиректов
                      <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">ИНФО</span>
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Эти поля служат как подсказка/шаблон в интерфейсе. API требует передавать
                      <code> success_url </code> и <code> failure_url </code> в каждом запросе; серверный фолбэк не используется.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Success URL (шаблон)
                        </label>
                        <input
                          type="url"
                          value={settings.shop1DefaultSuccessUrl || ''}
                          onChange={(e) => setSettings({ ...settings, shop1DefaultSuccessUrl: e.target.value })}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="https://your-shop.com/payment/success"
                        />
                        <p className="mt-1 text-xs text-gray-500">Рекомендуемый адрес для успешной оплаты (шаблон)</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Failure URL (шаблон)
                        </label>
                        <input
                          type="url"
                          value={settings.shop1DefaultFailureUrl || ''}
                          onChange={(e) => setSettings({ ...settings, shop1DefaultFailureUrl: e.target.value })}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="https://your-shop.com/payment/cancel"
                        />
                        <p className="mt-1 text-xs text-gray-500">Рекомендуемый адрес при отмене/ошибке (шаблон)</p>
                      </div>
                    </div>
                  </div>
                  {/* Callback Settings */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                      🔔 Настройки Callback уведомлений
                    </h4>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Callback URL по умолчанию
                      </label>
                      <input
                        type="url"
                        value={settings.shop1DefaultCallbackUrl || ''}
                        onChange={(e) => setSettings({ ...settings, shop1DefaultCallbackUrl: e.target.value })}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="https://your-shop.com/shop1/callback"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Подсказка/шаблон. API должен передавать <code> callback_url </code> в каждом запросе.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Максимум попыток отправки
                        </label>
                        <select
                          value={settings.shop1CallbackRetryMax || 10}
                          onChange={(e) => setSettings({ ...settings, shop1CallbackRetryMax: parseInt(e.target.value) })}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value={1}>1 попытка</option>
                          <option value={3}>3 попытки</option>
                          <option value={5}>5 попыток</option>
                          <option value={10}>10 попыток</option>
                          <option value={15}>15 попыток</option>
                          <option value={20}>20 попыток</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Задержка между попытками
                        </label>
                        <select
                          value={settings.shop1CallbackRetryBackoffMs || 30000}
                          onChange={(e) => setSettings({ ...settings, shop1CallbackRetryBackoffMs: parseInt(e.target.value) })}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value={5000}>5 секунд</option>
                          <option value={15000}>15 секунд</option>
                          <option value={30000}>30 секунд</option>
                          <option value={60000}>1 минута</option>
                          <option value={300000}>5 минут</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  {/* Documentation Link */}
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                    <h4 className="text-md font-medium text-blue-900 mb-2 flex items-center">
                      📚 Документация и примеры
                    </h4>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p><strong>Endpoints:</strong></p>
                      <ul className="list-disc list-inside ml-4 space-y-1">
                        <li><code>POST /api/shop1/create-request</code> - создание депозита</li>
                        <li><code>POST /api/shop1/create-withdrawal</code> - создание вывода</li>
                        <li><code>GET /api/shop1/withdrawal-status?billing_id=X</code> - статус вывода</li>
                      </ul>
                      <p className="mt-3"><strong>Аутентификация:</strong> Заголовки <code>X-API-Key</code> и <code>X-Sign</code></p>
                      <p><strong>Документ спецификации:</strong> <code>ShopMetod.md</code></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Настройки многоязычности (deprecated tab removed) */}
        {false && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Тексты (многоязычные)</h3>
              <LanguageTabs
                value={(settings.paymentPageLanguage as any) || 'ru'}
                onChange={(lang) => setSettings({ ...settings, paymentPageLanguage: lang })}
              />
            </div>

            {/* RU */}
            {((settings.paymentPageLanguage as any) || 'ru') === 'ru' && (
            <div className="card p-6">
              <h3 className="text-md font-medium text-gray-900 mb-4">Русский язык (RU)</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Заголовок формы</label>
                  <input
                    type="text"
                    value={settings.paymentPageFormHeaderRu || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageFormHeaderRu: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Введите данные для оплаты"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подзаголовок формы</label>
                  <textarea
                    value={settings.paymentPageFormSubheaderRu || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageFormSubheaderRu: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Заполните форму ниже для создания платежа"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подпись поля суммы</label>
                  <input
                    type="text"
                    value={settings.paymentPageAmountCaptionRu || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageAmountCaptionRu: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Сумма платежа"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подсказка для суммы</label>
                  <input
                    type="text"
                    value={settings.paymentPageAmountHintRu || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageAmountHintRu: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Минимальная сумма: 1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Заголовок быстрого выбора</label>
                  <input
                    type="text"
                    value={settings.paymentPageQuickSelectHeaderRu || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageQuickSelectHeaderRu: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Быстрый выбор суммы"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подпись поля комментария</label>
                  <input
                    type="text"
                    value={settings.paymentPageCommentCaptionRu || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageCommentCaptionRu: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Комментарий к платежу"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Кнопка отправки</label>
                  <input
                    type="text"
                    value={settings.paymentPageSubmitButtonRu || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageSubmitButtonRu: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Создать платеж"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Текст обработки</label>
                  <input
                    type="text"
                    value={settings.paymentPageProcessingRu || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageProcessingRu: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Обработка..."
                  />
                </div>
                {/* Футер (RU) */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Футер</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Текст футера</label>
                      <input
                        type="text"
                        value={settings.paymentPageFooterTextRu || ''}
                        onChange={(e) => setSettings({ ...settings, paymentPageFooterTextRu: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Например: Принимаем Visa/Mastercard"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: SSL</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSslRu || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSslRu: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="SSL защита"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: Безопасные платежи</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSecureRu || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSecureRu: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="Безопасные платежи"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: Поддержка</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSupportRu || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSupportRu: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="24/7 поддержка"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* KZ */}
            {((settings.paymentPageLanguage as any) || 'ru') === 'kz' && (
            <div className="card p-6">
              <h3 className="text-md font-medium text-gray-900 mb-4">Казахский язык (KZ)</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Заголовок формы</label>
                  <input
                    type="text"
                    value={settings.paymentPageFormHeaderKz || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageFormHeaderKz: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Төлем деректерін енгізіңіз"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подзаголовок формы</label>
                  <textarea
                    value={settings.paymentPageFormSubheaderKz || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageFormSubheaderKz: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Төлем жасау үшін төмендегі форманы толтырыңыз"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подпись поля суммы</label>
                  <input
                    type="text"
                    value={settings.paymentPageAmountCaptionKz || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageAmountCaptionKz: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Төлем сомасы"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подсказка для суммы</label>
                  <input
                    type="text"
                    value={settings.paymentPageAmountHintKz || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageAmountHintKz: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Минималды сома: 1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Заголовок быстрого выбора</label>
                  <input
                    type="text"
                    value={settings.paymentPageQuickSelectHeaderKz || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageQuickSelectHeaderKz: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Соманы жылдам таңдау"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подпись поля комментария</label>
                  <input
                    type="text"
                    value={settings.paymentPageCommentCaptionKz || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageCommentCaptionKz: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Төлем туралы түсініктеме"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Кнопка отправки</label>
                  <input
                    type="text"
                    value={settings.paymentPageSubmitButtonKz || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageSubmitButtonKz: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Төлем жасау"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Текст обработки</label>
                  <input
                    type="text"
                    value={settings.paymentPageProcessingKz || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageProcessingKz: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Өңделуде..."
                  />
                </div>
                {/* Футер (KZ) */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Футер</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Текст футера</label>
                      <input
                        type="text"
                        value={settings.paymentPageFooterTextKz || ''}
                        onChange={(e) => setSettings({ ...settings, paymentPageFooterTextKz: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Мысалы: Visa/Mastercard қабылдаймыз"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: SSL</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSslKz || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSslKz: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="SSL қорғау"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: Қауіпсіз төлемдер</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSecureKz || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSecureKz: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="Қауіпсіз төлемдер"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: Қолдау</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSupportKz || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSupportKz: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="24/7 қолдау"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
            {/* EN */}
            {((settings.paymentPageLanguage as any) || 'ru') === 'en' && (
            <div className="card p-6">
              <h3 className="text-md font-medium text-gray-900 mb-4">Английский язык (EN)</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Заголовок формы</label>
                  <input
                    type="text"
                    value={settings.paymentPageFormHeaderEn || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageFormHeaderEn: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Enter payment details"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подзаголовок формы</label>
                  <textarea
                    value={settings.paymentPageFormSubheaderEn || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageFormSubheaderEn: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Fill out the form below to create a payment"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подпись поля суммы</label>
                  <input
                    type="text"
                    value={settings.paymentPageAmountCaptionEn || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageAmountCaptionEn: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Payment amount"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подсказка для суммы</label>
                  <input
                    type="text"
                    value={settings.paymentPageAmountHintEn || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageAmountHintEn: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Minimum amount: 1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Заголовок быстрого выбора</label>
                  <input
                    type="text"
                    value={settings.paymentPageQuickSelectHeaderEn || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageQuickSelectHeaderEn: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Quick amount selection"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Подпись поля комментария</label>
                  <input
                    type="text"
                    value={settings.paymentPageCommentCaptionEn || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageCommentCaptionEn: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Payment comment"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Кнопка отправки</label>
                  <input
                    type="text"
                    value={settings.paymentPageSubmitButtonEn || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageSubmitButtonEn: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Create payment"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Текст обработки</label>
                  <input
                    type="text"
                    value={settings.paymentPageProcessingEn || ''}
                    onChange={(e) => setSettings({ ...settings, paymentPageProcessingEn: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Processing..."
                  />
                </div>
                {/* Footer (EN) */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Футер</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Текст футера</label>
                      <input
                        type="text"
                        value={settings.paymentPageFooterTextEn || ''}
                        onChange={(e) => setSettings({ ...settings, paymentPageFooterTextEn: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="e.g., We accept Visa/Mastercard"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: SSL</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSslEn || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSslEn: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="SSL secure"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: Безопасные платежи</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSecureEn || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSecureEn: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="Secure payments"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Бейдж: Поддержка</label>
                        <input
                          type="text"
                          value={settings.paymentPageFooterBadgeSupportEn || ''}
                          onChange={(e) => setSettings({ ...settings, paymentPageFooterBadgeSupportEn: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          placeholder="24/7 support"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        )}
        {/* Дизайн и тексты */}
        {activeTab === 'design-texts' && (
          <div className="card p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">🎨 Дизайн и тексты</h3>
              <div className="text-sm text-gray-500">Активный шаблон: <span className="font-semibold">{(settings.paymentPageDesign || 'simple') === 'simple' ? 'Simple UI' : 'Классический'}</span></div>
            </div>
            {/* Dev-only mapping check */}
            {process.env.NODE_ENV !== 'production' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      const r = await fetch(`/api/shop/${shopInfo?.id}/settings/mapping-check`);
                      const j = await r.json();
                      const unknown = Array.isArray(j.unknownKeys) ? j.unknownKeys.length : 0;
                      alert(`Design: ${j.design}\nUnknown keys: ${unknown}`);
                    } catch (e) {
                      alert('Mapping check failed');
                    }
                  }}
                  className="px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm"
                >
                  🔎 Проверка мапинга
                </button>
              </div>
            )}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button onClick={() => setDesignSubTab('common')} className={`py-2 px-1 border-b-2 font-medium text-sm ${designSubTab === 'common' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>⚙️ Общие настройки</button>
                <button onClick={() => setDesignSubTab('simple')} className={`py-2 px-1 border-b-2 font-medium text-sm ${designSubTab === 'simple' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>🧭 Настройки Дизайна SIMPLE UI {(settings.paymentPageDesign || 'simple') === 'simple' ? '— используется сейчас' : ''}</button>
                <button onClick={() => setDesignSubTab('classic')} className={`py-2 px-1 border-b-2 font-medium text-sm ${designSubTab === 'classic' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                  📦 Настройки Классического дизайна {(settings.paymentPageDesign || 'simple') === 'classic' ? '— используется сейчас' : ''}
                </button>
              </nav>
            </div>

            {designSubTab === 'common' && (
              <div className="space-y-6">
                {/* Общие — Тексты (без заголовка/подзаголовка для Simple) */}
                <div className="card p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" />
                  <LanguageTabs
                    value={(settings.paymentPageLanguage as any) || 'ru'}
                    onChange={(lang) => setSettings({ ...settings, paymentPageLanguage: lang })}
                  />

                  {/* Футер — общие (переключатель, иконка, ссылка, текст по умолчанию) */}
                  <div className="mt-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.paymentPageFooterEnabled !== false}
                        onChange={(e) => setSettings({ ...settings, paymentPageFooterEnabled: e.target.checked })}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Показывать футер на платежной странице</span>
                    </label>
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Иконка футера</label>
                      <input type="text" value={settings.paymentPageFooterIcon || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterIcon: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="например, 🛡️"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ссылка футера</label>
                      <input type="url" value={settings.paymentPageFooterLink || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterLink: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="https://example.com"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Текст футера по умолчанию</label>
                      <input type="text" value={settings.paymentPageFooterText || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterText: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="© 2025 Платежная система. Все права защищены."/>
                    </div>
                  </div>

                  {/* RU */}
                  {((settings.paymentPageLanguage as any) || 'ru') === 'ru' && (
                    <div className="space-y-4 mt-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Заголовок формы</label>
                          <input type="text" value={settings.paymentPageFormHeaderRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormHeaderRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подзаголовок формы</label>
                          <input type="text" value={settings.paymentPageFormSubheaderRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormSubheaderRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подпись суммы</label>
                          <input type="text" value={settings.paymentPageAmountCaptionRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountCaptionRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подсказка суммы</label>
                          <input type="text" value={settings.paymentPageAmountHintRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountHintRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Заголовок быстрого выбора</label>
                          <input type="text" value={settings.paymentPageQuickSelectHeaderRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageQuickSelectHeaderRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подпись комментария</label>
                          <input type="text" value={settings.paymentPageCommentCaptionRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageCommentCaptionRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Текст кнопки</label>
                          <input type="text" value={settings.paymentPageSubmitButtonRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSubmitButtonRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Текст обработки</label>
                          <input type="text" value={settings.paymentPageProcessingRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageProcessingRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Заголовок успеха</label>
                          <input type="text" value={settings.paymentPageSuccessTitleRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSuccessTitleRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Сообщение успеха</label>
                          <input type="text" value={settings.paymentPageSuccessMessageRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSuccessMessageRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>

                      {/* Футер (RU) */}
                      <div className="pt-4 border-t border-gray-200">
                        <h5 className="text-md font-medium text-gray-900 mb-2">Футер (RU)</h5>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Текст футера</label>
                            <input type="text" value={settings.paymentPageFooterTextRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterTextRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Например: Принимаем Visa/Mastercard"/>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Бейдж: SSL</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSslRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSslRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="SSL защита"/>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Бейдж: Безопасные платежи</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSecureRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSecureRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Безопасные платежи"/>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Бейдж: Поддержка</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSupportRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSupportRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="24/7 поддержка"/>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* KZ */}
                  {((settings.paymentPageLanguage as any) || 'ru') === 'kz' && (
                    <div className="space-y-4 mt-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Форма — заголовок (KZ)</label>
                          <input type="text" value={settings.paymentPageFormHeaderKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormHeaderKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Форма — подзаголовок (KZ)</label>
                          <input type="text" value={settings.paymentPageFormSubheaderKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormSubheaderKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подпись суммы (KZ)</label>
                          <input type="text" value={settings.paymentPageAmountCaptionKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountCaptionKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подсказка суммы (KZ)</label>
                          <input type="text" value={settings.paymentPageAmountHintKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountHintKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>

                      {/* Футер (KZ) */}
                      <div className="pt-4 border-t border-gray-200">
                        <h5 className="text-md font-medium text-gray-900 mb-2">Футер (KZ)</h5>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Текст футера</label>
                            <input type="text" value={settings.paymentPageFooterTextKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterTextKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Мысалы: Visa/Mastercard қабылдаймыз"/>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Бейдж: SSL</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSslKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSslKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="SSL қорғау"/>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Бейдж: Қауіпсіз төлемдер</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSecureKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSecureKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Қауіпсіз төлемдер"/>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Бейдж: Қолдау</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSupportKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSupportKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="24/7 қолдау"/>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* EN */}
                  {((settings.paymentPageLanguage as any) || 'ru') === 'en' && (
                    <div className="space-y-4 mt-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Form title</label>
                          <input type="text" value={settings.paymentPageFormHeaderEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormHeaderEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Form subtitle</label>
                          <input type="text" value={settings.paymentPageFormSubheaderEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormSubheaderEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount caption</label>
                          <input type="text" value={settings.paymentPageAmountCaptionEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountCaptionEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount hint</label>
                          <input type="text" value={settings.paymentPageAmountHintEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountHintEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>

                      {/* Footer (EN) */}
                      <div className="pt-4 border-t border-gray-200">
                        <h5 className="text-md font-medium text-gray-900 mb-2">Footer (EN)</h5>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Footer text</label>
                            <input type="text" value={settings.paymentPageFooterTextEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterTextEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="e.g., We accept Visa/Mastercard"/>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Badge: SSL</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSslEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSslEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="SSL secure"/>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Badge: Secure payments</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSecureEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSecureEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Secure payments"/>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Badge: Support</label>
                              <input type="text" value={settings.paymentPageFooterBadgeSupportEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFooterBadgeSupportEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="24/7 support"/>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Общие — Дизайн */}
                <div className="card p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Логотип</label>
                    <div className="flex items-center gap-2">
                      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e)=> setSelectedLogoFile(e.target.files?.[0] || null)} className="block w-full text-sm"/>
                      <button
                        type="button"
                        disabled={!selectedLogoFile || uploadingLogo}
                        onClick={async ()=>{
                          if (!selectedLogoFile) return;
                          try {
                            setUploadingLogo(true);
                            const fd = new FormData();
                            fd.append('logo', selectedLogoFile);
                            const res = await fetch('/api/admin/upload-logo', { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: fd });
                            const json = await res.json();
                            if (json?.success && json.url) {
                              setSettings({ ...settings, paymentPageLogoUploaded: json.url, paymentPageLogoEnabled: true });
                            } else {
                              alert('Не удалось загрузить логотип');
                            }
                          } catch (e) {
                            console.error('upload logo error', e);
                            alert('Ошибка загрузки логотипа');
                          } finally {
                            setUploadingLogo(false);
                            setSelectedLogoFile(null);
                          }
                        }}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
                      >{uploadingLogo ? 'Загрузка...' : 'Загрузить'}</button>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Или URL логотипа</label>
                      <input type="url" value={settings.paymentPageLogo || ''} onChange={(e)=> setSettings({ ...settings, paymentPageLogo: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="/uploads/logo_xxx.png или https://..."/>
                    </div>
                    <div className="flex items-center gap-3">
                      <input id="showLogo" type="checkbox" checked={settings.paymentPageLogoEnabled !== false} onChange={(e)=> setSettings({ ...settings, paymentPageLogoEnabled: e.target.checked })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"/>
                      <label htmlFor="showLogo" className="text-sm text-gray-700">Отображать логотип</label>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <input id="logoTransparentBg" type="checkbox" checked={(settings as any).paymentPageLogoTransparentBg === true} onChange={(e)=> setSettings({ ...settings, paymentPageLogoTransparentBg: e.target.checked })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"/>
                      <label htmlFor="logoTransparentBg" className="text-sm text-gray-700">Прозрачный фон под логотипом</label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Фон логотипа (форма)</label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500">Цвет фона страницы</label>
                        <input type="color" value={settings.paymentPageBackgroundColor || '#F8FAFC'} onChange={(e)=> setSettings({ ...settings, paymentPageBackgroundColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md"/>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Цвет карточек</label>
                        <input type="color" value={settings.paymentPageCardBg || '#FFFFFF'} onChange={(e)=> setSettings({ ...settings, paymentPageCardBg: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md"/>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Цвета применяются ко всем блокам, включая область логотипа.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Основной цвет</label>
                    <input type="color" value={settings.paymentPagePrimaryColor || '#3B82F6'} onChange={(e)=> setSettings({ ...settings, paymentPagePrimaryColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Цвет кнопок</label>
                    <input type="color" value={settings.paymentPageButtonColor || '#3B82F6'} onChange={(e)=> setSettings({ ...settings, paymentPageButtonColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Цвет текста</label>
                    <input type="color" value={settings.paymentPageTextColor || '#1F2937'} onChange={(e)=> setSettings({ ...settings, paymentPageTextColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Цвет карточек</label>
                    <input type="color" value={settings.paymentPageCardBg || '#FFFFFF'} onChange={(e)=> setSettings({ ...settings, paymentPageCardBg: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Цвет текста суммы</label>
                    <input type="color" value={settings.paymentPageAmountTextColor || '#2563EB'} onChange={(e)=> setSettings({ ...settings, paymentPageAmountTextColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md"/>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Цвет рамки срока действия</label>
                    <input type="color" value={settings.paymentPageExpiryBorderColor || '#FED7AA'} onChange={(e)=> setSettings({ ...settings, paymentPageExpiryBorderColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md"/>
                  </div>
                </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Кастомный CSS</label>
                      <textarea rows={4} value={settings.paymentPageCustomCSS || ''} onChange={(e)=> setSettings({ ...settings, paymentPageCustomCSS: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Кастомный JS</label>
                      <textarea rows={4} value={settings.paymentPageCustomJS || ''} onChange={(e)=> setSettings({ ...settings, paymentPageCustomJS: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs"/>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {designSubTab === 'simple' && (
              <div className="space-y-6">
                {/* Simple — Дизайн */}
                <div className="card p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">🎨 Дизайн (Simple)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Рамка у суммы</label>
                      <div className="flex items-center mt-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={(settings as any).paymentPageSimpleAmountBorderEnabled !== false}
                          onChange={(e)=> setSettings({ ...settings, paymentPageSimpleAmountBorderEnabled: e.target.checked })}
                        />
                        <span className="ml-2 text-sm text-gray-600">Показывать рамку у блока суммы</span>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Стиль номера шагов</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        value={(settings as any).paymentPageSimpleStepNumberStyle || 'numeric'}
                        onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStepNumberStyle: e.target.value as any })}
                      >
                        <option value="numeric">Арабские цифры</option>
                        <option value="roman">Римские цифры</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Плашка у номера шага</label>
                      <div className="flex items-center mt-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={(settings as any).paymentPageSimpleStepNumberAsBadge !== false}
                          onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStepNumberAsBadge: e.target.checked })}
                        />
                        <span className="ml-2 text-sm text-gray-600">Показывать синюю «плашку» у номера</span>
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700">Кнопка копирования — стиль</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                        <select
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                          value={(settings as any).paymentPageSimpleCopyButtonStyle || 'text'}
                          onChange={(e)=> setSettings({ ...settings, paymentPageSimpleCopyButtonStyle: e.target.value as any })}
                        >
                          <option value="text">Кнопка с текстом</option>
                          <option value="icon">Иконка</option>
                        </select>
                        <div className="flex items-center">
                          <label className="block text-sm font-medium text-gray-700 mr-3">Иконка</label>
                          <input type="text" className="block w-full border border-gray-300 rounded-md px-3 py-2" value={(settings as any).paymentPageSimpleCopyButtonIcon || '📋'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleCopyButtonIcon: e.target.value })} placeholder="например, 📋"/>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 1 — цвет текста</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={settings.paymentPageSimpleStep1Color || '#111827'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep1Color: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{settings.paymentPageSimpleStep1Color || '#111827'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 1 — цвет примечания</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={(settings as any).paymentPageSimpleStep1NoteColor || '#6B7280'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep1NoteColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{(settings as any).paymentPageSimpleStep1NoteColor || '#6B7280'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 2 — цвет текста</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={settings.paymentPageSimpleStep2Color || '#111827'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep2Color: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{settings.paymentPageSimpleStep2Color || '#111827'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 2 — цвет примечания</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={(settings as any).paymentPageSimpleStep2NoteColor || '#6B7280'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep2NoteColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{(settings as any).paymentPageSimpleStep2NoteColor || '#6B7280'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 3 — цвет текста</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={settings.paymentPageSimpleStep3Color || '#111827'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep3Color: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{settings.paymentPageSimpleStep3Color || '#111827'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 3 — цвет примечания</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={(settings as any).paymentPageSimpleStep3NoteColor || '#6B7280'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep3NoteColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{(settings as any).paymentPageSimpleStep3NoteColor || '#6B7280'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 4 — цвет текста</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={settings.paymentPageSimpleStep4Color || '#111827'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep4Color: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{settings.paymentPageSimpleStep4Color || '#111827'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 4 — цвет примечания</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={(settings as any).paymentPageSimpleStep4NoteColor || '#6B7280'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep4NoteColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{(settings as any).paymentPageSimpleStep4NoteColor || '#6B7280'}</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Имя получателя — цвет</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={settings.paymentPageSimpleRecipientColor || '#111827'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleRecipientColor: e.target.value })} className="mt-1 block w-12 h-10 border border-gray-300 rounded-md px-1 py-1"/>
                        <span className="text-xs text-gray-500">{settings.paymentPageSimpleRecipientColor || '#111827'}</span>
                      </div>
                    </div>
                  </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">URL кнопки подтверждения</label>
                      <input type="url" value={(settings as any).paymentPageConfirmButtonUrl || ''} onChange={(e)=> setSettings({ ...settings, paymentPageConfirmButtonUrl: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="https://shop.example/confirm"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Автопереход (сек.)</label>
                      <input type="number" min={1} max={10} value={Number((settings as any).paymentPageSimpleSuccessRedirectSeconds ?? 3)} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessRedirectSeconds: Math.max(1, Math.min(10, Number(e.target.value)||3)) })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">URL возврата</label>
                      <input type="url" value={(settings as any).paymentPageSimpleReturnUrl || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleReturnUrl: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="https://shop.example/return"/>
                    </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Если URL не указан</label>
                        <select value={(settings as any).paymentPageNoUrlBehavior || 'none'} onChange={(e)=> setSettings({ ...settings, paymentPageNoUrlBehavior: e.target.value as 'none' | 'close' | 'payment' })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                          <option value="none">Ничего не делать</option>
                          <option value="close">Закрыть страницу</option>
                          <option value="payment">Использовать success_url платежа</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">Применяется, если не задан URL кнопки подтверждения и URL возврата</p>
                      </div>
                  </div>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4">
                      <h5 className="text-md font-medium text-gray-900 mb-3">✅ Поп-ап успеха</h5>
                      <label className="block text-sm font-medium text-gray-700">Фон</label>
                      <input type="color" value={(settings as any).paymentPageSimpleSuccessBgColor || '#ECFDF5'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessBgColor: e.target.value })} className="mt-1 block w-28 h-10 border border-gray-300 rounded-md"/>
                      <label className="block text-sm font-medium text-gray-700 mt-3">Иконка</label>
                      <input type="text" value={(settings as any).paymentPageSimpleSuccessIcon || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessIcon: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="например, ✅"/>
                    </div>
                    <div className="border rounded-lg p-4">
                      <h5 className="text-md font-medium text-gray-900 mb-3">❌ Поп-ап ошибки</h5>
                      <label className="block text-sm font-medium text-gray-700">Фон</label>
                      <input type="color" value={(settings as any).paymentPageSimpleFailureBgColor || '#FEF2F2'} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleFailureBgColor: e.target.value })} className="mt-1 block w-28 h-10 border border-gray-300 rounded-md"/>
                      <label className="block text-sm font-medium text-gray-700 mt-3">Иконка</label>
                      <input type="text" value={(settings as any).paymentPageSimpleFailureIcon || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleFailureIcon: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" placeholder="например, ❌"/>
                    </div>
                  </div>
                </div>
                {/* Simple — Тексты */}
                <div className="card p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">📝 Тексты (Simple)</h4>
                  <LanguageTabs
                    value={(settings.paymentPageLanguage as any) || 'ru'}
                    onChange={(lang) => setSettings({ ...settings, paymentPageLanguage: lang })}
                  />
                  {/* Step 1 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 1 — заголовок (RU)</label>
                      <input type="text" value={settings.paymentPageSimpleStep1TitleRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep1TitleRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (RU)</label>
                      <input type="text" value={settings.paymentPageSimpleStep1NoteRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep1NoteRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 1 — заголовок (KZ)</label>
                      <input type="text" value={settings.paymentPageSimpleStep1TitleKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep1TitleKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (KZ)</label>
                      <input type="text" value={settings.paymentPageSimpleStep1NoteKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep1NoteKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 1 — заголовок (EN)</label>
                      <input type="text" value={settings.paymentPageSimpleStep1TitleEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep1TitleEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (EN)</label>
                      <input type="text" value={settings.paymentPageSimpleStep1NoteEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep1NoteEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 2 — заголовок (RU)</label>
                      <input type="text" value={settings.paymentPageSimpleStep2TitleRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep2TitleRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (RU)</label>
                      <input type="text" value={settings.paymentPageSimpleStep2NoteRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep2NoteRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 2 — заголовок (KZ)</label>
                      <input type="text" value={settings.paymentPageSimpleStep2TitleKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep2TitleKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (KZ)</label>
                      <input type="text" value={settings.paymentPageSimpleStep2NoteKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep2NoteKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 2 — заголовок (EN)</label>
                      <input type="text" value={settings.paymentPageSimpleStep2TitleEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep2TitleEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (EN)</label>
                      <input type="text" value={settings.paymentPageSimpleStep2NoteEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep2NoteEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 3 — заголовок (RU)</label>
                      <input type="text" value={settings.paymentPageSimpleStep3TitleRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep3TitleRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (RU)</label>
                      <input type="text" value={settings.paymentPageSimpleStep3NoteRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep3NoteRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 3 — заголовок (KZ)</label>
                      <input type="text" value={settings.paymentPageSimpleStep3TitleKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep3TitleKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (KZ)</label>
                      <input type="text" value={settings.paymentPageSimpleStep3NoteKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep3NoteKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 3 — заголовок (EN)</label>
                      <input type="text" value={settings.paymentPageSimpleStep3TitleEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep3TitleEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (EN)</label>
                      <input type="text" value={settings.paymentPageSimpleStep3NoteEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep3NoteEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 4 — заголовок (RU)</label>
                      <input type="text" value={settings.paymentPageSimpleStep4TitleRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep4TitleRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (RU)</label>
                      <input type="text" value={settings.paymentPageSimpleStep4NoteRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep4NoteRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 4 — заголовок (KZ)</label>
                      <input type="text" value={settings.paymentPageSimpleStep4TitleKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep4TitleKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (KZ)</label>
                      <input type="text" value={settings.paymentPageSimpleStep4NoteKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep4NoteKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Шаг 4 — заголовок (EN)</label>
                      <input type="text" value={settings.paymentPageSimpleStep4TitleEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep4TitleEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      <label className="block text-xs text-gray-500 mt-2">Примечание (EN)</label>
                      <input type="text" value={settings.paymentPageSimpleStep4NoteEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleStep4NoteEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                    </div>
                  </div>

                  {/* Тексты поп-апов (Simple) */}
                  <div className="mt-6">
                    <h5 className="text-md font-medium text-gray-900 mb-3">Тексты поп-апов (Simple)</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* RU */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Успех — заголовок (RU)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleSuccessTitleRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessTitleRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Успех — сообщение (RU)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleSuccessMessageRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessMessageRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Ошибка — заголовок (RU)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleFailureTitleRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleFailureTitleRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Ошибка — сообщение (RU)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleFailureMessageRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleFailureMessageRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                      {/* KZ */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Успех — заголовок (KZ)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleSuccessTitleKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessTitleKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Успех — сообщение (KZ)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleSuccessMessageKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessMessageKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Ошибка — заголовок (KZ)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleFailureTitleKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleFailureTitleKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Ошибка — сообщение (KZ)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleFailureMessageKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleFailureMessageKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                      {/* EN */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Success — title (EN)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleSuccessTitleEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessTitleEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Success — message (EN)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleSuccessMessageEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleSuccessMessageEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Failure — title (EN)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleFailureTitleEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleFailureTitleEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        <label className="block text-sm font-medium text-gray-700 mt-3">Failure — message (EN)</label>
                        <input type="text" value={(settings as any).paymentPageSimpleFailureMessageEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleFailureMessageEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                    </div>
                  </div>

                  {/* Инструкции */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Инструкция (RU)</label>
                    <textarea rows={3} value={settings.paymentPageSimpleInstructionRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleInstructionRu: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs" placeholder="Например: переводы в нерабочее время обрабатываются дольше" />
                    <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">Инструкция (KZ)</label>
                    <textarea rows={3} value={settings.paymentPageSimpleInstructionKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleInstructionKz: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs" placeholder="Мысалы: жұмыс емес уақытта аударымдар ұзақ өңделеді" />
                    <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">Инструкция (EN)</label>
                    <textarea rows={3} value={settings.paymentPageSimpleInstructionEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSimpleInstructionEn: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs" placeholder="E.g., transfers after hours may be processed slower" />
                  </div>
                </div>
              </div>
            )}
            {designSubTab === 'classic' && (
              <div className="space-y-6">
                {/* Classic — Дизайн (общие визуальные настройки уже доступны как Общие — Дизайн) */}
                <div className="card p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-2">🎨 Дизайн (Classic)</h4>
                  <p className="text-sm text-gray-500">Классический шаблон использует общие визуальные настройки (логотип, заголовок, цвета, кастомный CSS/JS).</p>
                  <div className="flex items-center mt-3">
                    <input
                      type="checkbox"
                      id="classicHeaderEnabled"
                      checked={settings.paymentPageHeaderEnabled !== false}
                      onChange={(e) => setSettings({ ...settings, paymentPageHeaderEnabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="classicHeaderEnabled" className="ml-2 block text-sm text-gray-900">
                      Показывать заголовок и подзаголовок страницы (Classic)
                    </label>
                    <p className="ml-6 text-sm text-gray-500">
                      Текущее состояние: {settings.paymentPageHeaderEnabled !== false ? 'включено' : 'выключено'}
                    </p>
                  </div>
                </div>
                {/* Classic — Тексты */}
                <div className="card p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">📝 Тексты (Classic)</h4>
                  <LanguageTabs
                    value={(settings.paymentPageLanguage as any) || 'ru'}
                    onChange={(lang) => setSettings({ ...settings, paymentPageLanguage: lang })}
                  />

                  {/* RU */}
                  {((settings.paymentPageLanguage as any) || 'ru') === 'ru' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Заголовок формы</label>
                        <input type="text" value={settings.paymentPageFormHeaderRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormHeaderRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Подзаголовок формы</label>
                        <textarea rows={2} value={settings.paymentPageFormSubheaderRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormSubheaderRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подпись суммы</label>
                          <input type="text" value={settings.paymentPageAmountCaptionRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountCaptionRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подсказка суммы</label>
                          <input type="text" value={settings.paymentPageAmountHintRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountHintRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Заголовок быстрого выбора</label>
                          <input type="text" value={settings.paymentPageQuickSelectHeaderRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageQuickSelectHeaderRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подпись комментария</label>
                          <input type="text" value={settings.paymentPageCommentCaptionRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageCommentCaptionRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Текст кнопки</label>
                          <input type="text" value={settings.paymentPageSubmitButtonRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSubmitButtonRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Текст обработки</label>
                          <input type="text" value={settings.paymentPageProcessingRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageProcessingRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Заголовок успеха</label>
                          <input type="text" value={settings.paymentPageSuccessTitleRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSuccessTitleRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Сообщение успеха</label>
                          <input type="text" value={settings.paymentPageSuccessMessageRu || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSuccessMessageRu: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* KZ */}
                  {((settings.paymentPageLanguage as any) || 'ru') === 'kz' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Форма — заголовок (KZ)</label>
                        <input type="text" value={settings.paymentPageFormHeaderKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormHeaderKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Форма — подзаголовок (KZ)</label>
                        <textarea rows={2} value={settings.paymentPageFormSubheaderKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormSubheaderKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подпись суммы (KZ)</label>
                          <input type="text" value={settings.paymentPageAmountCaptionKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountCaptionKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подсказка суммы (KZ)</label>
                          <input type="text" value={settings.paymentPageAmountHintKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountHintKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Заголовок быстрого выбора (KZ)</label>
                          <input type="text" value={settings.paymentPageQuickSelectHeaderKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageQuickSelectHeaderKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Подпись комментария (KZ)</label>
                          <input type="text" value={settings.paymentPageCommentCaptionKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageCommentCaptionKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Текст кнопки (KZ)</label>
                          <input type="text" value={settings.paymentPageSubmitButtonKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSubmitButtonKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Текст обработки (KZ)</label>
                          <input type="text" value={settings.paymentPageProcessingKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageProcessingKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Заголовок успеха (KZ)</label>
                          <input type="text" value={settings.paymentPageSuccessTitleKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSuccessTitleKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Сообщение успеха (KZ)</label>
                          <input type="text" value={settings.paymentPageSuccessMessageKz || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSuccessMessageKz: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* EN */}
                  {((settings.paymentPageLanguage as any) || 'ru') === 'en' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Form title</label>
                        <input type="text" value={settings.paymentPageFormHeaderEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormHeaderEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Form subtitle</label>
                        <textarea rows={2} value={settings.paymentPageFormSubheaderEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageFormSubheaderEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount caption</label>
                          <input type="text" value={settings.paymentPageAmountCaptionEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountCaptionEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount hint</label>
                          <input type="text" value={settings.paymentPageAmountHintEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageAmountHintEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Quick select header</label>
                          <input type="text" value={settings.paymentPageQuickSelectHeaderEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageQuickSelectHeaderEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Comment caption</label>
                          <input type="text" value={settings.paymentPageCommentCaptionEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageCommentCaptionEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Submit button text</label>
                          <input type="text" value={settings.paymentPageSubmitButtonEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSubmitButtonEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Processing text</label>
                          <input type="text" value={settings.paymentPageProcessingEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageProcessingEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Success title</label>
                          <input type="text" value={settings.paymentPageSuccessTitleEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSuccessTitleEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Success message</label>
                          <input type="text" value={settings.paymentPageSuccessMessageEn || ''} onChange={(e)=> setSettings({ ...settings, paymentPageSuccessMessageEn: e.target.value })} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"/>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Основные настройки страницы */}
            <div className="card p-6">
                          <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Основные настройки страницы</h3>
              <div className="flex space-x-2">
                <button
                  onClick={refreshSettings}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-sm"
                >
                  Обновить настройки
                </button>
              </div>
            </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Удалены настройки логотипа из Simple общего блока */}

                {false && (
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id="headerEnabled"
                      checked={settings.paymentPageHeaderEnabled !== false}
                      onChange={(e) => setSettings({ ...settings, paymentPageHeaderEnabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="headerEnabled" className="ml-2 block text-sm text-gray-900">
                      Показывать заголовок и подзаголовок страницы
                    </label>
                    <p className="ml-6 text-sm text-gray-500">
                      Текущее состояние: {settings.paymentPageHeaderEnabled !== false ? 'включено' : 'выключено'}
                    </p>
                  </div>
                )}

                {false && (
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id="publicIdEnabled"
                      checked={!!settings.paymentPublicIdEnabled}
                      onChange={(e) => setSettings({ ...settings, paymentPublicIdEnabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="publicIdEnabled" className="ml-2 block text-sm text-gray-900">
                      Использовать публичный код платежа (защита ссылок)
                    </label>
                    <p className="ml-6 text-sm text-gray-500">
                      Текущее состояние: {settings.paymentPublicIdEnabled ? 'включено' : 'выключено'}
                    </p>
                  </div>
                )}
                
                {false && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Тема оформления</label>
                    <div className="p-3 rounded border bg-yellow-50 text-yellow-800 text-sm">
                      Переключатель темы перенесён во вкладку «Основные».
                    </div>
                  </div>
                )}

                {false && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Дизайн платежной страницы</label>
                    <select
                      value={settings.paymentPageDesign || 'simple'}
                      onChange={(e) => setSettings({ ...settings, paymentPageDesign: e.target.value as any })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="simple">Простой (v2)</option>
                      <option value="classic">Классический (v1)</option>
                    </select>
                    <p className="mt-1 text-sm text-gray-500">По умолчанию используется "Простой (v2)".</p>
                  </div>
                )}

                {/* Simple (v2): дополнительные настройки */}
                {false && (
                  <div className="mt-6">
                    <div className="p-3 rounded border bg-blue-50 text-blue-800 text-sm">
                      Все настройки Simple теперь доступны во вкладке «Simple UI».
                    </div>
                  </div>
                )}
              </div>

              {/* Детальная цветовая схема */}
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">🎨 Цветовая схема</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* Основной цвет — скрываем для Simple дизайна */}
                  {((settings.paymentPageDesign || 'simple') !== 'simple') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Основной цвет</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          value={settings.paymentPagePrimaryColor || '#3B82F6'}
                          onChange={(e) => setSettings({ ...settings, paymentPagePrimaryColor: e.target.value })}
                          className="h-8 w-16 border border-gray-300 rounded"
                        />
                        <input
                          type="text"
                          value={settings.paymentPagePrimaryColor || '#3B82F6'}
                          onChange={(e) => setSettings({ ...settings, paymentPagePrimaryColor: e.target.value })}
                          className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Цвет кнопок */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет кнопок</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageButtonColor || '#3B82F6'}
                        onChange={(e) => setSettings({ ...settings, paymentPageButtonColor: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageButtonColor || '#3B82F6'}
                        onChange={(e) => setSettings({ ...settings, paymentPageButtonColor: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                  
                  {/* Цвет текста кнопок */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет текста кнопок</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageButtonTextColor || '#FFFFFF'}
                        onChange={(e) => setSettings({ ...settings, paymentPageButtonTextColor: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageButtonTextColor || '#FFFFFF'}
                        onChange={(e) => setSettings({ ...settings, paymentPageButtonTextColor: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                  {/* Цвет фона */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет фона</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageBackgroundColor || '#F8FAFC'}
                        onChange={(e) => setSettings({ ...settings, paymentPageBackgroundColor: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageBackgroundColor || '#F8FAFC'}
                        onChange={(e) => setSettings({ ...settings, paymentPageBackgroundColor: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                  
                  {/* Акцентный цвет — скрываем для Simple дизайна */}
                  {((settings.paymentPageDesign || 'simple') !== 'simple') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Акцентный цвет</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          value={settings.paymentPageAccentColor || '#10B981'}
                          onChange={(e) => setSettings({ ...settings, paymentPageAccentColor: e.target.value })}
                          className="h-8 w-16 border border-gray-300 rounded"
                        />
                        <input
                          type="text"
                          value={settings.paymentPageAccentColor || '#10B981'}
                          onChange={(e) => setSettings({ ...settings, paymentPageAccentColor: e.target.value })}
                          className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                        />
                      </div>
                    </div>
                  )}

                  {/* Цвет текста */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет текста</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageTextColor || '#1F2937'}
                        onChange={(e) => setSettings({ ...settings, paymentPageTextColor: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageTextColor || '#1F2937'}
                        onChange={(e) => setSettings({ ...settings, paymentPageTextColor: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>

                  {/* Вторичный цвет текста */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет вторичного текста</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageTextSecondary || '#6B7280'}
                        onChange={(e) => setSettings({ ...settings, paymentPageTextSecondary: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageTextSecondary || '#6B7280'}
                        onChange={(e) => setSettings({ ...settings, paymentPageTextSecondary: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>

                  {/* Цвет границ */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет границ</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageBorderColor || '#E5E7EB'}
                        onChange={(e) => setSettings({ ...settings, paymentPageBorderColor: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageBorderColor || '#E5E7EB'}
                        onChange={(e) => setSettings({ ...settings, paymentPageBorderColor: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>

                  {/* Цвет карточек */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет карточек</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageCardBg || '#FFFFFF'}
                        onChange={(e) => setSettings({ ...settings, paymentPageCardBg: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageCardBg || '#FFFFFF'}
                        onChange={(e) => setSettings({ ...settings, paymentPageCardBg: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                  {/* Предварительный просмотр */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Предварительный просмотр</label>
                    <div className="p-4 rounded-lg border" style={{ backgroundColor: settings.paymentPageBackgroundColor || '#F8FAFC' }}>
                      <button 
                        className="px-4 py-2 rounded-md text-sm font-medium transition-colors mr-2"
                        style={{ 
                          backgroundColor: settings.paymentPageButtonColor || '#3B82F6',
                          color: settings.paymentPageButtonTextColor || '#FFFFFF'
                        }}
                      >
                        Оплатить
                      </button>
                      <span 
                        className="text-sm"
                        style={{ color: settings.paymentPageAccentColor || '#10B981' }}
                      >
                        ✓ Безопасно
                      </span>
                      <div className="mt-3 p-3 rounded border" style={{ borderColor: settings.paymentPageBorderColor || '#E5E7EB', backgroundColor: settings.paymentPageCardBg || '#FFFFFF', color: settings.paymentPageTextColor || '#1F2937' }}>
                        <div className="text-xs" style={{ color: settings.paymentPageTextSecondary || '#6B7280' }}>Пример карточки</div>
                        Основной текст на карточке
                      </div>
                      <div className="mt-3">
                        <span className="text-sm" style={{ color: settings.paymentPageAmountTextColor || '#2563EB' }}>Пример суммы: 1 A0900  A0₸</span>
                      </div>
                      <div className="mt-3 p-2 rounded border" style={{ borderColor: settings.paymentPageExpiryBorderColor || '#FED7AA' }}>
                        <div className="text-xs" style={{ color: settings.paymentPageTextSecondary || '#6B7280' }}>Срок действия блока</div>
                      </div>
                    </div>
                  </div>
                  {/* Цвет рамки срока действия */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет рамки срока действия</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageExpiryBorderColor || '#FED7AA'}
                        onChange={(e) => setSettings({ ...settings, paymentPageExpiryBorderColor: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageExpiryBorderColor || '#FED7AA'}
                        onChange={(e) => setSettings({ ...settings, paymentPageExpiryBorderColor: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                  {/* Цвет суммы */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Цвет суммы</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.paymentPageAmountTextColor || '#2563EB'}
                        onChange={(e) => setSettings({ ...settings, paymentPageAmountTextColor: e.target.value })}
                        className="h-8 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.paymentPageAmountTextColor || '#2563EB'}
                        onChange={(e) => setSettings({ ...settings, paymentPageAmountTextColor: e.target.value })}
                        className="flex-1 border border-gray-300 rounded px-2 py-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Дополнительные опции */}
              <div className="mt-6 space-y-4">
                {((settings.paymentPageDesign || 'simple') !== 'simple') && (
                  <>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="showAmount"
                        checked={settings.paymentPageShowAmount || false}
                        onChange={(e) => setSettings({ ...settings, paymentPageShowAmount: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="showAmount" className="ml-2 block text-sm text-gray-900">
                        Показывать поле ввода суммы
                      </label>
                      <p className="ml-6 text-sm text-gray-500">
                        Текущее состояние: {settings.paymentPageShowAmount ? 'включено' : 'выключено'}
                      </p>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="showCurrency"
                        checked={settings.paymentPageShowCurrency || false}
                        onChange={(e) => setSettings({ ...settings, paymentPageShowCurrency: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="showCurrency" className="ml-2 block text-sm text-gray-900">
                        Показывать выбор валюты
                      </label>
                      <p className="ml-6 text-sm text-gray-500">
                        Текущее состояние: {settings.paymentPageShowCurrency ? 'включено' : 'выключено'}
                      </p>
                    </div>
                  </>
                )}

                {designSubTab === 'classic' && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="featuresEnabled"
                      checked={settings.paymentPageFeaturesEnabled !== false}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeaturesEnabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="featuresEnabled" className="ml-2 block text-sm text-gray-900">
                      Показывать плитки преимуществ (Classic)
                    </label>
                    <p className="ml-6 text-sm text-gray-500">
                      Текущее состояние: {settings.paymentPageFeaturesEnabled !== false ? 'включено' : 'выключено'}
                    </p>
                  </div>
                )}
              </div>
            </div>
            {false && (
              <div className="card p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Кастомизация</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Пользовательские CSS стили</label>
                    <textarea
                      value={settings.paymentPageCustomCSS || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageCustomCSS: e.target.value })}
                      rows={6}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                      placeholder="/* Ваши CSS стили */"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Пользовательские JavaScript</label>
                    <textarea
                      value={settings.paymentPageCustomJS || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageCustomJS: e.target.value })}
                      rows={6}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                      placeholder="// Ваш JavaScript код"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Управление плитками преимуществ — только Classic */}
        {activeTab === 'design-texts' && designSubTab === 'classic' && (
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Плитки преимуществ</h3>
            <p className="text-sm text-gray-600 mb-6">
              Настройте плитки с преимуществами, которые будут отображаться на платежной странице
            </p>
            
            <div className="space-y-6">
              {/* Плитка 1 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Плитка 1</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Русский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature1TitleRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1TitleRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на русском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature1SubtitleRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1SubtitleRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на русском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature1IconRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1IconRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Казахский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature1TitleKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1TitleKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на казахском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature1SubtitleKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1SubtitleKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на казахском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature1IconKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1IconKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Английский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature1TitleEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1TitleEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на английском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature1SubtitleEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1SubtitleEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на английском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature1IconEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature1IconEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                </div>
              </div>

              {/* Плитка 2 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Плитка 2</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Русский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature2TitleRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2TitleRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на русском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature2SubtitleRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2SubtitleRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на русском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature2IconRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2IconRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Казахский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature2TitleKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2TitleKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на казахском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature2SubtitleKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2SubtitleKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на казахском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature2IconKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2IconKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Английский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature2TitleEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2TitleEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на английском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature2SubtitleEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2SubtitleEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на английском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature2IconEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature2IconEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                </div>
              </div>
              {/* Плитка 3 */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">Плитка 3</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Русский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature3TitleRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3TitleRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на русском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature3SubtitleRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3SubtitleRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на русском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature3IconRu || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3IconRu: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Казахский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature3TitleKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3TitleKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на казахском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature3SubtitleKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3SubtitleKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на казахском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature3IconKz || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3IconKz: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Английский</label>
                    <input
                      type="text"
                      value={settings.paymentPageFeature3TitleEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3TitleEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Заголовок на английском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature3SubtitleEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3SubtitleEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Подзаголовок на английском"
                    />
                    <input
                      type="text"
                      value={settings.paymentPageFeature3IconEn || ''}
                      onChange={(e) => setSettings({ ...settings, paymentPageFeature3IconEn: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm mt-2"
                      placeholder="Иконка (эмодзи или текст)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Управление заготовками сумм — только Classic */}
        {activeTab === 'design-texts' && designSubTab === 'classic' && (
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Заготовки сумм</h3>
            <p className="text-sm text-gray-600 mb-6">
              Настройте кнопки с предустановленными суммами для быстрого выбора
            </p>
            
            <div className="space-y-4">
              {/* Минимальная сумма платежа */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="text-md font-medium text-gray-900 mb-3">💰 Минимальная сумма платежа</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Минимальная сумма (в тенге)</label>
                    <input
                      type="number"
                      min="1"
                      value={settings.paymentPageMinAmount || 100}
                      onChange={(e) => setSettings({ ...settings, paymentPageMinAmount: parseInt(e.target.value) || 100 })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="100"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Пользователи не смогут ввести сумму меньше указанной
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-white p-3 rounded border">
                      <p className="text-sm text-gray-600">
                        <strong>Текущее значение:</strong> {settings.paymentPageMinAmount || 100} тенге
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Минимальная сумма для всех платежей
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Кнопки сумм</label>
                <button
                  onClick={() => {
                    const newButtons = [...(settings.paymentPageAmountButtons || []), { amount: 1000, label: '' }];
                    setSettings({ ...settings, paymentPageAmountButtons: newButtons });
                  }}
                  className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm"
                >
                  + Добавить кнопку
                </button>
              </div>
              
              <div className="space-y-3">
                {(settings.paymentPageAmountButtons || []).map((button, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={button.amount}
                        onChange={(e) => {
                          const newButtons = [...(settings.paymentPageAmountButtons || [])];
                          newButtons[index].amount = parseInt(e.target.value) || 0;
                          setSettings({ ...settings, paymentPageAmountButtons: newButtons });
                        }}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="Сумма"
                        min="1"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={button.label}
                        onChange={(e) => {
                          const newButtons = [...(settings.paymentPageAmountButtons || [])];
                          newButtons[index].label = e.target.value;
                          setSettings({ ...settings, paymentPageAmountButtons: newButtons });
                        }}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="Подпись (необязательно)"
                      />
                    </div>
                    <button
                      onClick={() => {
                        const newButtons = (settings.paymentPageAmountButtons || []).filter((_, i) => i !== index);
                        setSettings({ ...settings, paymentPageAmountButtons: newButtons });
                      }}
                      className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm"
                    >
                      Удалить
                    </button>
                  </div>
                ))}
                
                {(!settings.paymentPageAmountButtons || ((settings.paymentPageAmountButtons || []).length === 0)) && (
                  <div className="text-sm text-gray-500">Кнопки не настроены</div>
                )}
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  💡 Совет: Добавьте 3-5 кнопок с популярными суммами для удобства пользователей
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Настройки страницы вывода */}
        {activeTab === 'withdraw-page' && (
          <div className="space-y-6">
            {/* Основные настройки */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">💸 Настройки страницы вывода средств</h3>
              <p className="text-sm text-gray-600 mb-6">
                Настройте внешний вид и тексты страницы для вывода средств клиентами
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Включение страницы вывода */}
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.withdrawPageEnabled}
                      onChange={(e) => setSettings({ ...settings, withdrawPageEnabled: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Включить веб-страницу вывода средств
                    </span>
                  </label>
                  <p className="mt-2 text-sm text-gray-600">
                    Предоставляет веб-интерфейс по адресу: <code>/withdraw/:publicId</code>
                  </p>
                </div>
                
                {/* Дизайн страницы */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Дизайн страницы вывода</label>
                  <select
                    value={settings.withdrawPageDesign || 'simple'}
                    onChange={(e) => setSettings({ ...settings, withdrawPageDesign: e.target.value as any })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="simple">Simple UI</option>
                    <option value="classic">Классический</option>
                  </select>
                </div>

                {/* Основной цвет */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Основной цвет</label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="color"
                      value={settings.withdrawPagePrimaryColor || '#10B981'}
                      onChange={(e) => setSettings({ ...settings, withdrawPagePrimaryColor: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={settings.withdrawPagePrimaryColor || '#10B981'}
                      onChange={(e) => setSettings({ ...settings, withdrawPagePrimaryColor: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder="#10B981"
                    />
                  </div>
                </div>

                {/* Цвет кнопок */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Цвет кнопок</label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="color"
                      value={settings.withdrawPageButtonColor || '#10B981'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageButtonColor: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={settings.withdrawPageButtonColor || '#10B981'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageButtonColor: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder="#10B981"
                    />
                  </div>
                </div>

                {/* Цвет текста кнопок */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Цвет текста кнопок</label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="color"
                      value={settings.withdrawPageButtonTextColor || '#FFFFFF'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageButtonTextColor: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={settings.withdrawPageButtonTextColor || '#FFFFFF'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageButtonTextColor: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder="#FFFFFF"
                    />
                  </div>
                </div>

                {/* Цвет текста страницы */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Цвет текста</label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="color"
                      value={settings.withdrawPageTextColor || '#1F2937'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageTextColor: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={settings.withdrawPageTextColor || '#1F2937'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageTextColor: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder="#1F2937"
                    />
                  </div>
                </div>

                {/* Фон страницы */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Фон страницы</label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="color"
                      value={settings.withdrawPageBackgroundColor || '#f9fafb'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageBackgroundColor: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={settings.withdrawPageBackgroundColor || '#f9fafb'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageBackgroundColor: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder="#f9fafb"
                    />
                  </div>
                </div>

                {/* Цвет фона карточки */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Фон карточки</label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="color"
                      value={(settings as any).withdrawPageCardBg || '#FFFFFF'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageCardBg: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={(settings as any).withdrawPageCardBg || '#FFFFFF'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageCardBg: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder="#FFFFFF"
                    />
                  </div>
                </div>

                {/* Цвет бордера карточки */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Цвет обводки карточки</label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="color"
                      value={(settings as any).withdrawPageBorderColor || (settings.withdrawPagePrimaryColor || '#10B981')}
                      onChange={(e) => setSettings({ ...settings, withdrawPageBorderColor: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={(settings as any).withdrawPageBorderColor || (settings.withdrawPagePrimaryColor || '#10B981')}
                      onChange={(e) => setSettings({ ...settings, withdrawPageBorderColor: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder="#10B981"
                    />
                  </div>
                </div>

                {/* Цвет текста суммы */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Цвет текста суммы</label>
                  <div className="mt-1 flex space-x-2">
                    <input
                      type="color"
                      value={settings.withdrawPageAmountTextColor || '#2563EB'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageAmountTextColor: e.target.value })}
                      className="w-16 h-10 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={settings.withdrawPageAmountTextColor || '#2563EB'}
                      onChange={(e) => setSettings({ ...settings, withdrawPageAmountTextColor: e.target.value })}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder="#2563EB"
                    />
                  </div>
                </div>

                {/* Логотип на странице вывода */}
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.withdrawPageLogoEnabled !== false}
                      onChange={(e) => setSettings({ ...settings, withdrawPageLogoEnabled: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Показывать логотип в верхней части страницы</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Используется загруженный логотип из раздела оплаты.</p>
                </div>

                {/* Футер на странице вывода */}
                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.withdrawPageFooterEnabled !== false}
                      onChange={(e) => setSettings({ ...settings, withdrawPageFooterEnabled: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Показывать футер на странице вывода</span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Скрывает блок с информацией о безопасности внизу страницы вывода.</p>
                </div>
              </div>
              {/* Блок настроек безопасности для страницы вывода удалён: настройки безопасности находятся в общих настройках */}
            </div>
            {/* Мультиязычные тексты */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">🌍 Тексты страницы (мультиязычность)</h3>
              
              {/* Заголовки страницы */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Заголовки страницы</h4>
                <LanguageTabs
                  value={withdrawLanguage}
                  onChange={setWithdrawLanguage}
                />
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Заголовок страницы ({withdrawLanguage === 'ru' ? 'Русский' : withdrawLanguage === 'kz' ? 'Казахский' : 'Английский'})
                    </label>
                    <input
                      type="text"
                      value={
                        withdrawLanguage === 'ru' ? settings.withdrawPageTitleRu || '' :
                        withdrawLanguage === 'kz' ? settings.withdrawPageTitleKz || '' :
                        settings.withdrawPageTitleEn || ''
                      }
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        [`withdrawPageTitle${withdrawLanguage === 'ru' ? 'Ru' : withdrawLanguage === 'kz' ? 'Kz' : 'En'}`]: e.target.value 
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder={
                        withdrawLanguage === 'ru' ? 'Вывод средств' :
                        withdrawLanguage === 'kz' ? 'Ақшаны шығару' :
                        'Withdraw Funds'
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Подзаголовок страницы ({withdrawLanguage === 'ru' ? 'Русский' : withdrawLanguage === 'kz' ? 'Казахский' : 'Английский'})
                    </label>
                    <input
                      type="text"
                      value={
                        withdrawLanguage === 'ru' ? settings.withdrawPageSubtitleRu || '' :
                        withdrawLanguage === 'kz' ? settings.withdrawPageSubtitleKz || '' :
                        settings.withdrawPageSubtitleEn || ''
                      }
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        [`withdrawPageSubtitle${withdrawLanguage === 'ru' ? 'Ru' : withdrawLanguage === 'kz' ? 'Kz' : 'En'}`]: e.target.value 
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder={
                        withdrawLanguage === 'ru' ? 'Введите данные для вывода' :
                        withdrawLanguage === 'kz' ? 'Шығару үшін деректерді енгізіңіз' :
                        'Enter data for withdrawal'
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Заголовки формы */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Заголовки формы</h4>
                <LanguageTabs
                  value={withdrawLanguage}
                  onChange={setWithdrawLanguage}
                />
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Заголовок формы ({withdrawLanguage === 'ru' ? 'Русский' : withdrawLanguage === 'kz' ? 'Казахский' : 'Английский'})
                    </label>
                    <input
                      type="text"
                      value={
                        withdrawLanguage === 'ru' ? settings.withdrawPageFormHeaderRu || '' :
                        withdrawLanguage === 'kz' ? settings.withdrawPageFormHeaderKz || '' :
                        settings.withdrawPageFormHeaderEn || ''
                      }
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        [`withdrawPageFormHeader${withdrawLanguage === 'ru' ? 'Ru' : withdrawLanguage === 'kz' ? 'Kz' : 'En'}`]: e.target.value 
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder={
                        withdrawLanguage === 'ru' ? 'Данные для вывода' :
                        withdrawLanguage === 'kz' ? 'Шығару үшін деректер' :
                        'Withdrawal details'
                      }
                    />
                  </div>
                </div>
              </div>
              {/* Кнопки */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Тексты кнопок</h4>
                <LanguageTabs
                  value={withdrawLanguage}
                  onChange={setWithdrawLanguage}
                />
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Кнопка отправки ({withdrawLanguage === 'ru' ? 'Русский' : withdrawLanguage === 'kz' ? 'Казахский' : 'Английский'})
                    </label>
                    <input
                      type="text"
                      value={
                        withdrawLanguage === 'ru' ? settings.withdrawPageSubmitButtonRu || '' :
                        withdrawLanguage === 'kz' ? settings.withdrawPageSubmitButtonKz || '' :
                        settings.withdrawPageSubmitButtonEn || ''
                      }
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        [`withdrawPageSubmitButton${withdrawLanguage === 'ru' ? 'Ru' : withdrawLanguage === 'kz' ? 'Kz' : 'En'}`]: e.target.value 
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder={
                        withdrawLanguage === 'ru' ? 'Запросить вывод' :
                        withdrawLanguage === 'kz' ? 'Шығаруды сұрау' :
                        'Request withdrawal'
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Кнопка редактирования ({withdrawLanguage === 'ru' ? 'Русский' : withdrawLanguage === 'kz' ? 'Казахский' : 'Английский'})
                    </label>
                    <input
                      type="text"
                      value={
                        withdrawLanguage === 'ru' ? settings.withdrawPageEditButtonRu || '' :
                        withdrawLanguage === 'kz' ? settings.withdrawPageEditButtonKz || '' :
                        settings.withdrawPageEditButtonEn || ''
                      }
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        [`withdrawPageEditButton${withdrawLanguage === 'ru' ? 'Ru' : withdrawLanguage === 'kz' ? 'Kz' : 'En'}`]: e.target.value 
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder={
                        withdrawLanguage === 'ru' ? 'Изменить данные' :
                        withdrawLanguage === 'kz' ? 'Деректерді өзгерту' :
                        'Edit details'
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Сообщения статусов */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Сообщения для пользователей</h4>
                <LanguageTabs
                  value={withdrawLanguage}
                  onChange={setWithdrawLanguage}
                />
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Сообщение для существующего пользователя ({withdrawLanguage === 'ru' ? 'Русский' : withdrawLanguage === 'kz' ? 'Казахский' : 'Английский'})
                    </label>
                    <input
                      type="text"
                      value={
                        withdrawLanguage === 'ru' ? settings.withdrawPageExistingUserMessageRu || '' :
                        withdrawLanguage === 'kz' ? settings.withdrawPageExistingUserMessageKz || '' :
                        settings.withdrawPageExistingUserMessageEn || ''
                      }
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        [`withdrawPageExistingUserMessage${withdrawLanguage === 'ru' ? 'Ru' : withdrawLanguage === 'kz' ? 'Kz' : 'En'}`]: e.target.value 
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder={
                        withdrawLanguage === 'ru' ? 'Мы нашли ваши данные:' :
                        withdrawLanguage === 'kz' ? 'Біз сіздің деректеріңізді таптық:' :
                        'We found your details:'
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Сообщение для нового пользователя ({withdrawLanguage === 'ru' ? 'Русский' : withdrawLanguage === 'kz' ? 'Казахский' : 'Английский'})
                    </label>
                    <input
                      type="text"
                      value={
                        withdrawLanguage === 'ru' ? settings.withdrawPageNewUserMessageRu || '' :
                        withdrawLanguage === 'kz' ? settings.withdrawPageNewUserMessageKz || '' :
                        settings.withdrawPageNewUserMessageEn || ''
                      }
                      onChange={(e) => setSettings({ 
                        ...settings, 
                        [`withdrawPageNewUserMessage${withdrawLanguage === 'ru' ? 'Ru' : withdrawLanguage === 'kz' ? 'Kz' : 'En'}`]: e.target.value 
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder={
                        withdrawLanguage === 'ru' ? 'Укажите реквизиты для первого вывода:' :
                        withdrawLanguage === 'kz' ? 'Алғашқы шығару үшін деректерді көрсетіңіз:' :
                        'Enter details for your first withdrawal:'
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Информация об API */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-md font-medium text-blue-900 mb-2">📡 API для интеграции</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Инициализация вывода:</strong> <code>POST /api/withdrawal/init</code></p>
                  <p><strong>Веб-страница:</strong> <code>/withdraw/:publicId</code></p>
                  <p><strong>Формат publicId:</strong> <code>wdr_1234567890_abc123xyz</code></p>
                  <p className="mt-2 text-blue-700">
                    💡 Магазин отправляет username и amount, получает ссылку для клиента
                  </p>
                </div>
              </div>
            </div>

            {/* Пользовательские стили */}
            <div className="card p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">🎨 Пользовательские стили</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Пользовательские CSS</label>
                  <textarea
                    value={settings.withdrawPageCustomCSS || ''}
                    onChange={(e) => setSettings({ ...settings, withdrawPageCustomCSS: e.target.value })}
                    rows={8}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                    placeholder="/* Ваши CSS стили для страницы вывода */"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Пользовательские JavaScript</label>
                  <textarea
                    value={settings.withdrawPageCustomJS || ''}
                    onChange={(e) => setSettings({ ...settings, withdrawPageCustomJS: e.target.value })}
                    rows={8}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                    placeholder="// Ваш JavaScript код для страницы вывода"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Telegram связи */}
        {activeTab === 'telegram' && <TelegramLinks />}

        {/* Кнопка сохранения - скрыта для вкладки Telegram */}
        {activeTab !== 'telegram' && (
          <div className="flex justify-end">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Сохранение...' : 'Сохранить настройки'}
            </button>
          </div>
        )}
      </div>

      {/* Модальное окно с новым API ключом */}
      {showApiKeyModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Новый API ключ</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  API ключ успешно изменен. Сохраните новый ключ в безопасном месте.
                </p>
                <div className="bg-gray-100 p-3 rounded-md">
                  <code className="text-sm break-all">{newApiKey}</code>
                </div>
                <p className="text-xs text-red-600 mt-2">
                  ⚠️ Старый ключ больше не действителен!
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => setShowApiKeyModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-700"
                >
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopSettings;